import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Calendar, ChevronLeft, ChevronRight, RefreshCw, MapPin, Target, Plus, Ticket, Search } from 'lucide-react';
import apiService from '../services/apiService';
import TicketSidebar from './TicketSidebar';
import { useToast } from './ToastProvider';
import TicketCustomerField from './TicketCustomerField';
import CalendarTicketTitle from './CalendarTicketTitle';
import TicketDetail from './TicketDetail';
import CalendarEventDetail from './CalendarEventDetail';
import { CalenderMarkerHeaderSelect } from '../utils/calenderMarkerUtils';
import KalenderTicketKennung, { kennungPlatz, hatKennung } from './kalender/KalenderTicketKennung';
import { GebuchtMenuePunkt, useGebuchtUmschalten } from './kalender/Gebucht';
import EventToTicketModal from './EventToTicketModal';
import { useI18n } from '../utils/i18n';
import Modal from './ui/Modal';
import { flagInvalidField } from '../utils/formValidation';
import { useConfirm } from './ui/ConfirmDialog';
import * as loadingDebug from '../utils/loadingDebug';
import useIsMobile from '../hooks/useIsMobile';
import { erstelleKalenderHelfer } from '../utils/kalenderHelfer';

// #308: stabile Rueckfallzelle -- ein Objektliteral im Render waere bei
// jedem Durchlauf ein neues.
const LEERE_ZELLE = { items: [], hasAbove: false, hasBelow: false };

const CalendarView = () => {
  // W6 (#16): TicketDetail bekommt auf schmalen Screens das kompakte Mobil-Layout
  const isMobileScreen = useIsMobile();
  const { pushToast } = useToast();
  // #604: „Gebucht“ aus dem Kontextmenü, danach die Woche neu laden
  const gebuchtUmschalten = useGebuchtUmschalten(async () => {
    const { startOfWeek, endOfWeek } = getCurrentWeekRange();
    await loadCalendarDataForDateRange(startOfWeek, endOfWeek);
  });
  const { t, getLocale } = useI18n();
  const confirm = useConfirm();
  // Calendar data state
  const [events, setEvents] = useState([]);
  const [plannedTickets, setPlannedTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekStart, setWeekStart] = useState(new Date());
  
  // Current time marker state
  const [currentTime, setCurrentTime] = useState(new Date());
  
  const [draggedTicket, setDraggedTicket] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Operation state to prevent concurrent drag operations
  const [operationInProgress, setOperationInProgress] = useState(false);
  const [operationMessage, setOperationMessage] = useState('');

  // Ticket Detail Modal (bestehend vom Dashboard)
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  // Event Detail Modal
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDetail, setShowEventDetail] = useState(false);
  // Drop-Entscheidungs-Modal (Verschieben vs. weiteren erstellen)
  const [dropChoiceModal, setDropChoiceModal] = useState({ visible: false, context: null });
  
  // Ticket Creation Form
  const [showCreateTicketForm, setShowCreateTicketForm] = useState(false);
  // removed unused availableEmails
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'normal',
    category: '',
    tags: [],
    calenderMarker: null,
    customerId: null,
    contactId: null
  });
  
  // Context Menu State
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    ticket: null
  });
  // Event-Kontextmenü (Graph-Termine)
  const [eventContextMenu, setEventContextMenu] = useState({ visible: false, x: 0, y: 0, event: null });
  const [showEventConvertModal, setShowEventConvertModal] = useState(false);
  const [selectedEventForConvert, setSelectedEventForConvert] = useState(null);
  
  
  

  
  // Drag state für geplante Tickets
  const [draggedPlannedTicket, setDraggedPlannedTicket] = useState(null);
  const [dragStartPos, setDragStartPos] = useState(null);
  
  // Live Drag Preview State
  const [dragPreview, setDragPreview] = useState(null);
  const [showDragPreview, setShowDragPreview] = useState(false);
  
  // Resize State
  const [, setResizingTicket] = useState(null);
  const [, setResizeStartPos] = useState({ x: 0, y: 0 });
  const [, setResizeStartHeight] = useState(0);
  const [, setResizeHandle] = useState(null);
  const [resizePreview, setResizePreview] = useState(null);
  const [showResizePreview, setShowResizePreview] = useState(false);
  const [justResized, setJustResized] = useState(false);
  const [resizeMode, setResizeMode] = useState(false);
  const [showExtendedWeek, setShowExtendedWeek] = useState(false);
  // Dynamic time window (defaults to 8-18)
  const [timeStartHour, setTimeStartHour] = useState(8);
  const [timeEndHour, setTimeEndHour] = useState(18);
  // #311: die gemeinsamen Kalender-Helfer aus utils/kalenderHelfer.js.
  // Arbeitszeitfenster und Sprache sind gebunden, damit die Aufrufstellen
  // unveraendert bleiben.
  const {
    getUserTimeZone, formatTime, formatTicketTime, getItemTimeRange,
    snapToGrid, extractTicketNumberFromEvent, extractSeriesIdFromEvent, parseBlockSuffix,
    getEventStatus, getTicketColor, isToday, getTimeSlots,
    getEventHeight, getTicketHeight, getEventPosition, getTicketPosition,
    getCurrentTimePosition, calculateItemPositions, tagesGrenzen, tagesEintraege,
    erstelleStundenFormat, ausserhalbFenster
  } = useMemo(
    () => erstelleKalenderHelfer({ timeStartHour, timeEndHour, locale: getLocale() }),
    [timeStartHour, timeEndHour, getLocale]
  );

  
  // Search state (find in calendar)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]); // { type: 'event'|'ticket', id, start: Date }
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const [matchedEventIds, setMatchedEventIds] = useState([]);
  const [matchedTicketIds, setMatchedTicketIds] = useState([]);
  const [currentMatch, setCurrentMatch] = useState(null); // { type, id }
  
  // Reference to store current resize values
  const currentResizeValues = useRef({ startDateTime: null, endDateTime: null });
  
  // References for event listeners cleanup
  const resizeMouseMoveRef = useRef(null);
  const resizeMouseUpRef = useRef(null);
  
  // Mouse selection refs for creating tickets directly from calendar
  const selectionMouseMoveRef = useRef(null);
  const selectionMouseUpRef = useRef(null);
  const selectionContainerRectRef = useRef(null);
  const selectionStartYRef = useRef(0);
  
  // Reference to store the ticket removal function from TicketSidebar
  const removeTicketFromSidebar = useRef(null);

  // Mouse selection state for creating tickets directly from calendar
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionDay, setSelectionDay] = useState(null);
  const [selectionRect, setSelectionRect] = useState(null); // { topPercent, heightPercent }
  const [pendingPlannedTimeRange, setPendingPlannedTimeRange] = useState(null); // { startDateTime, endDateTime }

  // Callback to register the ticket functions from TicketSidebar
  const handleTicketPlannedCallback = (ticketFunctions) => {
    removeTicketFromSidebar.current = ticketFunctions;
  };

  // Get ISO week number
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Cleanup function for resize event listeners
  const cleanupResizeListeners = () => {
    if (resizeMouseMoveRef.current) {
      document.removeEventListener('mousemove', resizeMouseMoveRef.current);
      resizeMouseMoveRef.current = null;
    }
    if (resizeMouseUpRef.current) {
      document.removeEventListener('mouseup', resizeMouseUpRef.current);
      resizeMouseUpRef.current = null;
    }
  };


  useEffect(() => {
    calculateWeekStart();
  }, [currentDate]);

  // Load current user once on component mount
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const userResponse = await apiService.getCurrentUser();
        setCurrentUser(userResponse);
      } catch (err) {
        console.error('Error loading current user:', err);
      }
    };
    loadCurrentUser();
  }, []);



  // Cleanup effect for resize event listeners
  useEffect(() => {
    return () => {
      cleanupResizeListeners();
      if (selectionMouseMoveRef.current) {
        document.removeEventListener('mousemove', selectionMouseMoveRef.current);
        selectionMouseMoveRef.current = null;
      }
      if (selectionMouseUpRef.current) {
        document.removeEventListener('mouseup', selectionMouseUpRef.current);
        selectionMouseUpRef.current = null;
      }
    };
  }, []);

  // Close context menu when clicking outside
  // #189: zusätzlich contextmenu (Capture) — Rechtsklick feuert kein click,
  // dadurch blieb z. B. das Ticket-Menü beim Rechtsklick auf einen
  // Outlook-Termin offen (und umgekehrt).
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu.visible) {
        setContextMenu({ visible: false, x: 0, y: 0, ticket: null });
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('contextmenu', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('contextmenu', handleClickOutside, true);
    };
  }, [contextMenu.visible]);

  // Close Event-ContextMenu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (eventContextMenu.visible) {
        setEventContextMenu({ visible: false, x: 0, y: 0, event: null });
      }
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('contextmenu', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('contextmenu', handleClickOutside, true);
    };
  }, [eventContextMenu.visible]);

  const calculateWeekStart = () => {
    const date = new Date(currentDate);
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Monday as first day of week
    date.setDate(date.getDate() + diff);
    date.setHours(0, 0, 0, 0);
    setWeekStart(date);
  };

  // Calculate current week range (memoized to prevent infinite loops)
  const getCurrentWeekRange = useCallback(() => {
    const startOfWeek = new Date(weekStart);
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(weekStart);
    // If extended week is enabled, show 14 days, otherwise 7 days
    const daysToShow = showExtendedWeek ? 13 : 6; // 13 days after start = 14 total, 6 days after start = 7 total
    endOfWeek.setDate(weekStart.getDate() + daysToShow);
    endOfWeek.setHours(23, 59, 59, 999);
    
    return { startOfWeek, endOfWeek };
  }, [weekStart, showExtendedWeek]);

  const loadEventsForWeek = useCallback(async () => {
    // Calculate week range fresh each time to avoid dependency issues
    const { startOfWeek, endOfWeek } = getCurrentWeekRange();
    
    setLoading(true);
    setError(null);
    
    try {
      // Loading debug tasks for calendar
      try {
        loadingDebug.start('cal-events', 'Kalender: Termine laden', 'o365');
        loadingDebug.start('cal-planned', 'Kalender: Geplante Tickets laden', 'local');
      } catch (_) {}
      // Load events and planned tickets in parallel
      const [eventsResponse, plannedTicketsResponse] = await Promise.all([
        apiService.getEvents(startOfWeek, endOfWeek),
        apiService.getPlannedTickets(startOfWeek, endOfWeek)
      ]);
      
      const events = eventsResponse.value || [];
      const tickets = plannedTicketsResponse.tickets || [];
      
      // Filter tickets to only show those assigned to current user or unassigned
      const filteredTickets = tickets.filter(ticket => {
        if (!currentUser) return true; // Show all if no user loaded yet
        
        const isAssignedToCurrentUser = ticket.assignedTo === currentUser.username || 
                                       ticket.assignedTo === currentUser.userPrincipalName;
        const isUnassigned = !ticket.assignedTo || ticket.assignedTo === '';
        
        return isAssignedToCurrentUser || isUnassigned;
      });
      
      setEvents(events);
      setPlannedTickets(filteredTickets);
      try {
        loadingDebug.done('cal-events', true);
        loadingDebug.done('cal-planned', true);
      } catch (_) {}
      
      // Debug: Log mehrtägige Termine
      const multiDayTickets = filteredTickets.filter(t => t.planningSource === 'new');
      if (multiDayTickets.length > 0) {
        console.log('📅 [CALENDAR] Multi-day tickets loaded:', multiDayTickets.map(t => ({
          id: t.id,
          originalId: t.originalTicketId,
          title: t.title.substring(0, 20),
          plannedStart: t.plannedStartDateTime
        })));
      }
      
    } catch (error) {
      try {
        loadingDebug.done('cal-events', false);
        loadingDebug.done('cal-planned', false);
      } catch (_) {}
      if (error.response?.status === 401) {
        setError(t('common.sessionExpired', 'Sitzung abgelaufen. Bitte melden Sie sich erneut an.'));
        setTimeout(() => {
          try {
            const raw = String(document.cookie || '');
            const allowAutoSSO = raw.split(';').some(c => c.trim().startsWith('was_logged_in=1'));
            window.location.href = allowAutoSSO ? '/auth/sso' : '/auth/login';
          } catch (_) {
            window.location.href = '/auth/login';
          }
        }, 3000);
      } else {
        setError(t('calendar.errorLoad', 'Fehler beim Laden der Termine. Bitte versuchen Sie es später erneut.'));
      }
    } finally {
      setLoading(false);
    }
  }, [weekStart, getCurrentWeekRange, currentUser]);

  // Perform calendar search over a broad range and navigate to first match
  const performSearch = useCallback(async (query) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) {
      setMatchedEventIds([]);
      setMatchedTicketIds([]);
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      setCurrentMatch(null);
      return;
    }

    // Broad search window: +/-365 days
    const now = new Date();
    const rangeStart = new Date(now);
    rangeStart.setDate(rangeStart.getDate() - 365);
    rangeStart.setHours(0, 0, 0, 0);
    const rangeEnd = new Date(now);
    rangeEnd.setDate(rangeEnd.getDate() + 365);
    rangeEnd.setHours(23, 59, 59, 999);

    try {
      const plannedTicketsResponse = await apiService.getPlannedTickets(rangeStart, rangeEnd);
      const allTicketsRaw = plannedTicketsResponse.tickets || [];

      // Apply same ticket filter as calendar view (current user or unassigned)
      const filteredTickets = allTicketsRaw.filter(ticket => {
        if (!currentUser) return true;
        const isAssignedToCurrentUser = ticket.assignedTo === currentUser.username || 
                                       ticket.assignedTo === currentUser.userPrincipalName;
        const isUnassigned = !ticket.assignedTo || ticket.assignedTo === '';
        return isAssignedToCurrentUser || isUnassigned;
      });

      const results = [];
      const tokens = q.split(/\s+/).filter(Boolean);
      filteredTickets.forEach(t => {
        const title = (t.title || '').toLowerCase();
        if (tokens.every(tok => title.includes(tok))) {
          const start = t.plannedStartDateTime ? new Date(t.plannedStartDateTime) : new Date();
          results.push({ type: 'ticket', id: t.id, start });
        }
      });

      results.sort((a, b) => a.start - b.start);
      setSearchResults(results);
      setMatchedEventIds([]);
      setMatchedTicketIds(results.filter(r => r.type === 'ticket').map(r => r.id));

      if (results.length > 0) {
        const nowMs = Date.now();
        let firstIndex = results.findIndex(r => r.start && r.start.getTime() >= nowMs);
        if (firstIndex === -1) firstIndex = results.length - 1; // all in past -> pick latest
        const first = results[firstIndex];
        setCurrentSearchIndex(firstIndex);
        setCurrentMatch({ type: first.type, id: first.id });

        // Navigate to week containing the first match
        const targetDate = new Date(first.start);
        const dow = targetDate.getDay();
        const diffToMonday = (dow === 0 ? -6 : 1 - dow);
        const newWeekStart = new Date(targetDate);
        newWeekStart.setDate(targetDate.getDate() + diffToMonday);
        newWeekStart.setHours(0, 0, 0, 0);
        const s = new Date(newWeekStart);
        const e = new Date(newWeekStart);
        const daysToShow = showExtendedWeek ? 13 : 6;
        e.setDate(newWeekStart.getDate() + daysToShow);
        e.setHours(23, 59, 59, 999);

        setCurrentDate(targetDate);
        try { await loadCalendarDataForDateRange(s, e); } catch (_) {}
        // Do not auto-scroll
      } else {
        setCurrentSearchIndex(-1);
        setCurrentMatch(null);
      }
    } catch (e) {
      console.error('📅 [CALENDAR] Error during global search:', e);
    }
  }, [currentUser, showExtendedWeek]);

  const goToNextSearchResult = useCallback(async () => {
    if (!searchResults || searchResults.length === 0) return;
    if (currentSearchIndex >= searchResults.length - 1) return;
    const nextIndex = currentSearchIndex + 1;
    const next = searchResults[nextIndex];
    setCurrentSearchIndex(nextIndex);
    setCurrentMatch({ type: next.type, id: next.id });

    // Navigate if outside current visible range
    try {
      const { startOfWeek, endOfWeek } = getCurrentWeekRange();
      const targetDate = new Date(next.start);
      if (targetDate < startOfWeek || targetDate > endOfWeek) {
        const dow = targetDate.getDay();
        const diffToMonday = (dow === 0 ? -6 : 1 - dow);
        const newWeekStart = new Date(targetDate);
        newWeekStart.setDate(targetDate.getDate() + diffToMonday);
        newWeekStart.setHours(0, 0, 0, 0);
        const s = new Date(newWeekStart);
        const e = new Date(newWeekStart);
        const daysToShow = showExtendedWeek ? 13 : 6;
        e.setDate(newWeekStart.getDate() + daysToShow);
        e.setHours(23, 59, 59, 999);
        setCurrentDate(targetDate);
        await loadCalendarDataForDateRange(s, e);
      }
    } catch (_) {}

    // No auto scroll on stepping through results
  }, [searchResults, currentSearchIndex, getCurrentWeekRange, showExtendedWeek]);

  const goToPrevSearchResult = useCallback(async () => {
    if (!searchResults || searchResults.length === 0) return;
    if (currentSearchIndex <= 0) return;
    const prevIndex = currentSearchIndex - 1;
    const prev = searchResults[prevIndex];
    setCurrentSearchIndex(prevIndex);
    setCurrentMatch({ type: prev.type, id: prev.id });

    try {
      const { startOfWeek, endOfWeek } = getCurrentWeekRange();
      const targetDate = new Date(prev.start);
      if (targetDate < startOfWeek || targetDate > endOfWeek) {
        const dow = targetDate.getDay();
        const diffToMonday = (dow === 0 ? -6 : 1 - dow);
        const newWeekStart = new Date(targetDate);
        newWeekStart.setDate(targetDate.getDate() + diffToMonday);
        newWeekStart.setHours(0, 0, 0, 0);
        const s = new Date(newWeekStart);
        const e = new Date(newWeekStart);
        const daysToShow = showExtendedWeek ? 13 : 6;
        e.setDate(newWeekStart.getDate() + daysToShow);
        e.setHours(23, 59, 59, 999);
        setCurrentDate(targetDate);
        await loadCalendarDataForDateRange(s, e);
      }
    } catch (_) {}

    // No auto scroll on stepping through results
  }, [searchResults, currentSearchIndex, getCurrentWeekRange, showExtendedWeek]);

  // Load events only on initial load and user changes (navigation uses optimized refresh)
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  useEffect(() => {
    if (currentUser && isInitialLoad) {
      loadEventsForWeek().then(() => {
        setIsInitialLoad(false);
      });
    }
  }, [loadEventsForWeek, currentUser, isInitialLoad]);

  // Reload calendar data when showExtendedWeek changes (to load additional days)
  useEffect(() => {
    if (currentUser && !isInitialLoad) {
      console.log('📅 [CALENDAR] Extended week mode changed, silent refresh...');
      // Use silent refresh to avoid loading state and page reload
      // Don't include loadCalendarDataSilently in dependency array to avoid circular dependency
      const silentRefresh = async () => {
        try {
          const { startOfWeek, endOfWeek } = getCurrentWeekRange();
          
          const [eventsResponse, plannedTicketsResponse] = await Promise.all([
            apiService.getEvents(startOfWeek, endOfWeek),
            apiService.getPlannedTickets(startOfWeek, endOfWeek)
          ]);
          
          const newEvents = eventsResponse.value || [];
          const newTickets = plannedTicketsResponse.tickets || [];
          
          const filteredNewTickets = newTickets.filter(ticket => {
            if (!currentUser) return true;
            
            const isAssignedToCurrentUser = ticket.assignedTo === currentUser.username || 
                                           ticket.assignedTo === currentUser.userPrincipalName;
            const isUnassigned = !ticket.assignedTo || ticket.assignedTo === '';
            
            return isAssignedToCurrentUser || isUnassigned;
          });
          
          setEvents(newEvents);
          setPlannedTickets(filteredNewTickets);
          
          console.log('📅 [CALENDAR] Silent refresh completed for extended week change');
        } catch (error) {
          console.error('📅 [CALENDAR] Error during silent refresh:', error);
        }
      };
      
      silentRefresh();
    }
  }, [showExtendedWeek, currentUser, isInitialLoad, getCurrentWeekRange]);





  // Background refresh function for silent updates without interrupting user (similar to GlobalCalendarView)
  const backgroundRefreshCalendarData = useCallback(async () => {
    try {
      // Calculate week range
      const { startOfWeek, endOfWeek } = getCurrentWeekRange();
      
      // Load events and tickets silently
      const [eventsResponse, plannedTicketsResponse] = await Promise.all([
        apiService.getEvents(startOfWeek, endOfWeek),
        apiService.getPlannedTickets(startOfWeek, endOfWeek)
      ]);
      
      const newEvents = eventsResponse.value || [];
      const newTickets = plannedTicketsResponse.tickets || [];
      
      // Filter tickets to only show those assigned to current user or unassigned
      const filteredNewTickets = newTickets.filter(ticket => {
        if (!currentUser) return true;
        
        const isAssignedToCurrentUser = ticket.assignedTo === currentUser.username || 
                                       ticket.assignedTo === currentUser.userPrincipalName;
        const isUnassigned = !ticket.assignedTo || ticket.assignedTo === '';
        
        return isAssignedToCurrentUser || isUnassigned;
      });
      
      // Check if there are actual changes by comparing with current state
      let hasChanges = false;
      
      // Compare events
      if (events.length !== newEvents.length) {
        hasChanges = true;
      } else {
        // Compare event IDs and times
        const currentEventMap = new Map(events.map(e => [e.id, `${e.start?.dateTime}-${e.end?.dateTime}`]));
        const newEventMap = new Map(newEvents.map(e => [e.id, `${e.start?.dateTime}-${e.end?.dateTime}`]));
        
        for (const [id, timeSignature] of newEventMap) {
          if (!currentEventMap.has(id) || currentEventMap.get(id) !== timeSignature) {
            hasChanges = true;
            break;
          }
        }
      }
      
      // Compare tickets
      if (!hasChanges) {
        if (plannedTickets.length !== filteredNewTickets.length) {
          hasChanges = true;
        } else {
          // Compare ticket IDs and times (verwende eindeutige Kombination für mehrtägige Termine)
          const currentTicketMap = new Map(plannedTickets.map(t => [
            `${t.originalTicketId || t.id}-${t.plannedStartDateTime}`,
            `${t.plannedStartDateTime}-${t.plannedEndDateTime}`
          ]));
          const newTicketMap = new Map(filteredNewTickets.map(t => [
            `${t.originalTicketId || t.id}-${t.plannedStartDateTime}`,
            `${t.plannedStartDateTime}-${t.plannedEndDateTime}`
          ]));
          
          for (const [id, timeSignature] of newTicketMap) {
            if (!currentTicketMap.has(id) || currentTicketMap.get(id) !== timeSignature) {
              hasChanges = true;
              break;
            }
          }
        }
      }
      
      // Only update state if there are actual changes
      if (hasChanges) {
        console.log('📅 [CALENDAR] Background refresh detected changes, updating calendar silently...');
        setEvents(newEvents);
        setPlannedTickets(filteredNewTickets);
        console.log('📅 [CALENDAR] Background refresh completed with updates');
      } else {
        console.log('📅 [CALENDAR] Background refresh completed, no changes detected');
      }
      
    } catch (error) {
      console.error('📅 [CALENDAR] Error during background refresh:', error);
      // Don't fallback to full reload for background refresh to avoid interrupting user
    }
  }, [getCurrentWeekRange, currentUser, events, plannedTickets]);

  // Navigation-specific calendar data refresh - uses explicit date range
  const loadCalendarDataForDateRange = useCallback(async (startOfWeek, endOfWeek) => {
    try {
      // Load events and tickets silently
      const [eventsResponse, plannedTicketsResponse] = await Promise.all([
        apiService.getEvents(startOfWeek, endOfWeek),
        apiService.getPlannedTickets(startOfWeek, endOfWeek)
      ]);
      
      const newEvents = eventsResponse.value || [];
      const newTickets = plannedTicketsResponse.tickets || [];
      
      // Filter tickets to only show those assigned to current user or unassigned
      const filteredNewTickets = newTickets.filter(ticket => {
        if (!currentUser) return true;
        
        const isAssignedToCurrentUser = ticket.assignedTo === currentUser.username || 
                                       ticket.assignedTo === currentUser.userPrincipalName;
        const isUnassigned = !ticket.assignedTo || ticket.assignedTo === '';
        
        return isAssignedToCurrentUser || isUnassigned;
      });
      
      // Update calendar data directly without loading state
      setEvents(newEvents);
      setPlannedTickets(filteredNewTickets);
      
      console.log('📅 [CALENDAR] Navigation refresh completed for date range');
      
    } catch (error) {
      console.error('📅 [CALENDAR] Error during navigation refresh:', error);
      // Fallback to full refresh on error
      await loadEventsForWeek();
    }
  }, [currentUser, loadEventsForWeek]);

  // Silent calendar data refresh function - updates calendar without setting loading state
  const loadCalendarDataSilently = useCallback(async () => {
    try {
      // Calculate week range
      const { startOfWeek, endOfWeek } = getCurrentWeekRange();
      
      // Load events and tickets silently
      const [eventsResponse, plannedTicketsResponse] = await Promise.all([
        apiService.getEvents(startOfWeek, endOfWeek),
        apiService.getPlannedTickets(startOfWeek, endOfWeek)
      ]);
      
      const newEvents = eventsResponse.value || [];
      const newTickets = plannedTicketsResponse.tickets || [];
      
      // Filter tickets to only show those assigned to current user or unassigned
      const filteredNewTickets = newTickets.filter(ticket => {
        if (!currentUser) return true;
        
        const isAssignedToCurrentUser = ticket.assignedTo === currentUser.username || 
                                       ticket.assignedTo === currentUser.userPrincipalName;
        const isUnassigned = !ticket.assignedTo || ticket.assignedTo === '';
        
        return isAssignedToCurrentUser || isUnassigned;
      });
      
      // Update calendar data directly without loading state
      setEvents(newEvents);
      setPlannedTickets(filteredNewTickets);
      
      console.log('📅 [CALENDAR] Silent calendar refresh completed');
      
    } catch (error) {
      console.error('📅 [CALENDAR] Error during silent calendar refresh:', error);
    }
  }, [getCurrentWeekRange, currentUser]);

  // Start background synchronization every 10 minutes
  useEffect(() => {
    const syncInterval = setInterval(async () => {
      // Only sync when page is visible and user is loaded
      if (currentUser && document.visibilityState === 'visible') {
        try {
          console.log('📅 [CALENDAR] Starting background synchronization...');
          const syncResult = await apiService.syncTicketCalendar();
          if (syncResult.success && (syncResult.results.removedPlanning > 0 || syncResult.results.updatedTimes > 0)) {
            console.log('📅 [CALENDAR] Background sync found changes - reloading calendar');
            // Silently reload the calendar to reflect changes
            await loadEventsForWeek();
          } else {
            console.log('📅 [CALENDAR] Background sync completed - no changes detected');
          }
        } catch (error) {
          console.error('📅 [CALENDAR] Error during background synchronization:', error);
        }
      }
    }, 600000); // 10 minutes

    return () => clearInterval(syncInterval);
  }, [currentUser, loadEventsForWeek]);

  // Start background refresh every 60 seconds (similar to GlobalCalendarView)
  useEffect(() => {
    let refreshInterval;
    
    if (currentUser) {
      refreshInterval = setInterval(async () => {
        // Only refresh when page is visible and we have users and no operation in progress
        if (document.visibilityState === 'visible' && !operationInProgress) {
          await backgroundRefreshCalendarData();
        }
      }, 60000); // 60 seconds for frequent updates
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [currentUser, backgroundRefreshCalendarData, operationInProgress]);

  // Update current time every 10 minutes for time marker
  useEffect(() => {
    // Initial time set
    setCurrentTime(new Date());
    
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(timeInterval);
  }, []);



  const getWeekDays = useCallback(() => {
    const days = [];
    const start = new Date(weekStart);
    
    // If extended view is enabled, show 14 days (2 weeks), otherwise 7 days
    const dayCount = showExtendedWeek ? 14 : 7;
    
    for (let i = 0; i < dayCount; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    
    return days;
  }, [weekStart, showExtendedWeek]);

  const getVisibleDays = useCallback(() => {
    const allDays = getWeekDays();
    
    if (showExtendedWeek) {
      // When extended week is enabled, show all 14 days
      return allDays;
    } else {
      // Filter out Saturday (6) and Sunday (0) from the 7-day view
      return allDays.filter(day => {
        const dayOfWeek = day.getDay();
        return dayOfWeek !== 0 && dayOfWeek !== 6; // 0 = Sunday, 6 = Saturday
      });
    }
  }, [getWeekDays, showExtendedWeek]);

  const navigateWeek = async (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
    
    // Use optimized navigation refresh if user is already loaded
    if (currentUser && !isInitialLoad) {
      // Calculate week range for new date directly
      const newWeekStart = new Date(newDate);
      const day = newWeekStart.getDay();
      const diff = day === 0 ? -6 : 1 - day; // Monday as first day of week
      newWeekStart.setDate(newWeekStart.getDate() + diff);
      newWeekStart.setHours(0, 0, 0, 0);
      
      const startOfWeek = new Date(newWeekStart);
      const endOfWeek = new Date(newWeekStart);
      // Adjust end date based on extended week setting
      const daysToShow = showExtendedWeek ? 13 : 6; // 13 days after start = 14 total, 6 days after start = 7 total
      endOfWeek.setDate(newWeekStart.getDate() + daysToShow);
      endOfWeek.setHours(23, 59, 59, 999);
      
      // Load calendar data for new week range
      setTimeout(async () => {
        await loadCalendarDataForDateRange(startOfWeek, endOfWeek);
      }, 10);
    }
  };

  const goToToday = async () => {
    const newDate = new Date();
    setCurrentDate(newDate);
    
    // Use optimized navigation refresh if user is already loaded
    if (currentUser && !isInitialLoad) {
      // Calculate week range for today directly
      const newWeekStart = new Date(newDate);
      const day = newWeekStart.getDay();
      const diff = day === 0 ? -6 : 1 - day; // Monday as first day of week
      newWeekStart.setDate(newWeekStart.getDate() + diff);
      newWeekStart.setHours(0, 0, 0, 0);
      
      const startOfWeek = new Date(newWeekStart);
      const endOfWeek = new Date(newWeekStart);
      // Adjust end date based on extended week setting
      const daysToShow = showExtendedWeek ? 13 : 6; // 13 days after start = 14 total, 6 days after start = 7 total
      endOfWeek.setDate(newWeekStart.getDate() + daysToShow);
      endOfWeek.setHours(23, 59, 59, 999);
      
      // Load calendar data for new week range
      setTimeout(async () => {
        await loadCalendarDataForDateRange(startOfWeek, endOfWeek);
      }, 10);
    }
  };

  // Pure sidebar refresh function - only for TicketSidebar updates (no calendar impact)
  const handleSidebarOnlyRefresh = () => {
    // This function is called by TicketSidebar as onRefresh prop
    // TicketSidebar will handle its own refresh via loadUnplannedTickets()
    // We don't need to do anything calendar-related here
    console.log('📅 [CALENDAR] TicketSidebar refresh triggered (no calendar update needed)');
  };

  const isCurrentWeek = () => {
    const today = new Date();
    const weekDays = getWeekDays();
    return weekDays.some(day => day.toDateString() === today.toDateString());
  };


  // Filter out Graph events that have corresponding tickets
  // NEW: Only filter the chronologically latest duplicate, keep earlier ones
  const filterDuplicateGraphEvents = useCallback((items) => {
    const tickets = items.filter(item => item.type === 'ticket');
    const events = items.filter(item => item.type === 'event');

    // Get all ticket numbers and calendar event IDs from tickets
    const ticketNumbers = new Set(tickets.map(item => item.data.ticketNumber));
    const calendarEventIds = new Set(tickets.map(item => item.data.calendarEventId).filter(Boolean));

    // Group duplicate events by their duplicate criteria to find the latest ones to filter
    const duplicateGroups = new Map();

    events.forEach(eventItem => {
      const event = eventItem.data;
      let duplicateKey = null;

      // Check if this event ID matches any ticket's calendarEventId exactly
      if (calendarEventIds.has(event.id)) {
        duplicateKey = `calendar-id:${event.id}`;
      }

      // Check if event body contains a ticket reference that matches an existing ticket
      const ticketNumberFromEvent = extractTicketNumberFromEvent(event.body);
      if (ticketNumberFromEvent && ticketNumbers.has(ticketNumberFromEvent)) {
        duplicateKey = `ticket-number:${ticketNumberFromEvent}`;
      }

      // Check if event has a series ID that should be filtered
      const seriesIdFromEvent = extractSeriesIdFromEvent(event.body);
      if (seriesIdFromEvent) {
        // Check if any tickets in this day have the same series ID in their events
        for (const ticketItem of tickets) {
          if (ticketItem.data.ticketNumber === ticketNumberFromEvent) {
            duplicateKey = `series-id:${seriesIdFromEvent}`;
            break;
          }
        }
      }

      // If this event is a duplicate, add it to the appropriate group
      if (duplicateKey) {
        if (!duplicateGroups.has(duplicateKey)) {
          duplicateGroups.set(duplicateKey, []);
        }
        duplicateGroups.get(duplicateKey).push(eventItem);
      }
    });

    // Determine which events to filter out (only the latest in each duplicate group)
    const eventsToFilter = new Set();

    duplicateGroups.forEach((duplicateEvents) => {
      if (duplicateEvents.length > 1) {
        // Sort by start time (latest first)
        const sortedDuplicates = duplicateEvents.sort((a, b) => {
          const timeA = getItemTimeRange(a);
          const timeB = getItemTimeRange(b);
          return timeB.start - timeA.start; // Latest first
        });

        // Only filter out the latest (first in sorted array), keep the earlier ones
        eventsToFilter.add(sortedDuplicates[0].data.id);
      } else if (duplicateEvents.length === 1) {
        // Single duplicate, filter it out as before
        eventsToFilter.add(duplicateEvents[0].data.id);
      }
    });

    // Filter out only the events marked for filtering
    const filteredEvents = events.filter(eventItem => !eventsToFilter.has(eventItem.data.id));

    // Return all tickets plus filtered events
    return [...tickets, ...filteredEvents];
  }, [extractTicketNumberFromEvent, extractSeriesIdFromEvent, getItemTimeRange]);

  // #308: Die Tagesspalten einmal je Datenlage statt einmal je Render.
  //
  // Der Auftrag hielt CalendarView fuer unkritisch, weil hier nur 5-7 Spalten
  // stehen (gegen 45-63 im globalen Kalender). Gemessen waren es trotzdem
  // 20,5 ms je Render -- 123 % des Budgets bei 60 Bildern/s. Der Grund lag
  // nicht bei calculateItemPositions, sondern daneben: die Pruefung "gibt es
  // etwas ausserhalb des Zeitfensters" legte sich ihr Intl.DateTimeFormat
  // INNERHALB der some()-Rueckrufe an, also einmal je Eintrag statt einmal.
  const visibleDays = useMemo(() => getVisibleDays(), [getVisibleDays]);

  const tagesZellen = useMemo(() => {
    const karte = new Map();
    const hourFmt = erstelleStundenFormat();
    visibleDays.forEach((day) => {
      const { dayStart, dayEnd } = tagesGrenzen(day);
      const allItems = tagesEintraege(events, plannedTickets, dayStart, dayEnd);
      karte.set(day.getTime(), {
        items: calculateItemPositions(filterDuplicateGraphEvents(allItems)),
        ...ausserhalbFenster(allItems, hourFmt)
      });
    });
    return karte;
  }, [visibleDays, events, plannedTickets, tagesGrenzen, tagesEintraege, ausserhalbFenster,
      erstelleStundenFormat, calculateItemPositions, filterDuplicateGraphEvents]);

  const renderCalendarItem = (item, index) => {
    const itemPosition = item.position || {
      column: 0,
      totalColumns: 1,
      left: 2,
      width: 'calc(100% - 4px)'
    };

    if (item.type === 'event') {
      const event = item.data;
      const status = getEventStatus(event.showAs);
      const position = getEventPosition(event);
      const height = getEventHeight(event);
      const isSearchMatched = matchedEventIds.includes(event.id);
      const isCurrent = currentMatch && currentMatch.type === 'event' && currentMatch.id === event.id;
      // Adjust top so late items anchor to bottom if out of range
      let topPercent = position;
      try {
        const startUtcDateTime = event.start.dateTime.endsWith('Z') ? event.start.dateTime : event.start.dateTime + 'Z';
        const start = new Date(startUtcDateTime);
        const userTimeZone = getUserTimeZone();
        const timeFormat = new Intl.DateTimeFormat('de-DE', { timeZone: userTimeZone, hour: 'numeric', hour12: false });
        const startHour = parseInt(timeFormat.format(start));
        if (startHour >= timeEndHour) {
          topPercent = Math.max(0, 100 - height);
        } else if (startHour < timeStartHour) {
          topPercent = 0;
        }
      } catch (e) {
        // Fallback: use computed position
      }
      
      return (
        <div
          key={`event-${event.id}`}
          className="calendar-event calendar-event-positioned"
          style={{
            position: 'absolute',
            top: `${topPercent}%`,
            left: itemPosition.left,
            width: itemPosition.width,
            height: `${height}%`,
            backgroundColor: isSearchMatched ? 'var(--color-search-hit)' : status.color,
            color: 'white',
            padding: '2px 4px',
            borderRadius: '3px',
            fontSize: '10px',
            lineHeight: '1.1',
            cursor: 'pointer',
            zIndex: 10,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            border: isCurrent ? '3px solid var(--color-search-hit)' : undefined,
            boxShadow: isCurrent ? '0 0 0 3px var(--color-search-hit-glow-60), 0 0 12px var(--color-search-hit-glow-90)' : undefined
          }}
          data-item-id={`event-${event.id}`}
          title={`${event.subject}\n${formatTime(event.start.dateTime)} - ${formatTime(event.end.dateTime)}\n${event.location?.displayName || ''}`}
          onClick={(e) => handleEventClick(e, event)}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setEventContextMenu({ visible: true, x: e.clientX, y: e.clientY, event });
          }}
        >
          <div style={{ fontWeight: 'bold', fontSize: '10px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {event.subject || t('global.noSubject', '(Kein Betreff)')}
          </div>
          <div style={{ fontSize: '8px', opacity: 0.9 }}>
            {formatTime(event.start.dateTime)}
          </div>
          {event.location?.displayName && height > 15 && (
            <div style={{ fontSize: '8px', opacity: 0.8, marginTop: '1px' }}>
              <MapPin size={6} style={{ marginRight: '1px', verticalAlign: 'middle' }} />
              {event.location.displayName.substring(0, 15)}
            </div>
          )}
          {(() => {
            try {
              const provider = String(event?.onlineMeetingProvider || '').toLowerCase();
              const joinUrl = event?.onlineMeeting?.joinUrl || event?.onlineMeetingUrl || null;
              const isTeams = Boolean((event?.isOnlineMeeting && provider.includes('teams')) || (joinUrl && /https?:\/\/(.*\.)?teams\.microsoft\.com/i.test(joinUrl)));
              if (!isTeams) return null;
              return (
                <div
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1px',
                    zIndex: 30,
                    pointerEvents: 'none',
                    backgroundColor: 'var(--color-glass-90)',
                    borderRadius: '3px',
                    boxShadow: '0 1px 3px var(--color-shadow-30)'
                  }}
                  title={t('eventDetail.teamsMarker', 'Teams-Besprechung')}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      lineHeight: '12px',
                      display: 'block',
                      textAlign: 'center'
                    }}
                  >
                    🎥
                  </span>
                </div>
              );
            } catch (_) { return null; }
          })()}
        </div>
      );
    } else {
      const ticket = item.data;
      const color = ticket.isArchived ? getEventStatus('busy').color : getTicketColor(ticket);
      const position = getTicketPosition(ticket);
      const height = getTicketHeight(ticket);
      const isDragging = draggedPlannedTicket?.id === ticket.id;
      const isSearchMatched = matchedTicketIds.includes(ticket.id);
      const isCurrent = currentMatch && currentMatch.type === 'ticket' && currentMatch.id === ticket.id;
      
      // Adjust top so late items anchor to bottom if out of range
      let topPercent = position;
      try {
        const start = new Date(ticket.plannedStartDateTime);
        const userTimeZone = getUserTimeZone();
        const timeFormat = new Intl.DateTimeFormat('de-DE', { timeZone: userTimeZone, hour: 'numeric', hour12: false });
        const startHour = parseInt(timeFormat.format(start));
        if (startHour >= timeEndHour) {
          topPercent = Math.max(0, 100 - height);
        } else if (startHour < timeStartHour) {
          topPercent = 0;
        }
      } catch (e) {}
      
      return (
        <div
          key={`ticket-${ticket.id}`}
          className={`calendar-event calendar-event-positioned calendar-ticket ${isDragging ? 'dragging' : ''} ${operationInProgress ? 'operation-blocked' : ''} ${hatKennung(ticket) ? 'has-markers' : ''} ${ticket.isArchived ? 'archived-ticket' : ''}`}
          style={{
            position: 'absolute',
            top: `${topPercent}%`,
            left: itemPosition.left,
            width: itemPosition.width,
            height: `${height}%`,
            backgroundColor: isSearchMatched ? 'var(--color-search-hit)' : color,
            color: 'white',
            padding: '2px 4px',
            paddingRight: kennungPlatz(ticket), // #604: Platz für Marker, Teams und Prioritätspunkt
            borderRadius: '3px',
            fontSize: '10px',
            lineHeight: '1.1',
            cursor: 'pointer',
            zIndex: isDragging ? 15 : 10,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            border: isCurrent ? '3px solid var(--color-search-hit)' : '2px solid var(--color-glass-50)',
            boxShadow: isCurrent ? '0 0 0 3px var(--color-search-hit-glow-60), 0 0 12px var(--color-search-hit-glow-90)' : undefined,
            opacity: isDragging ? 0.5 : 1
          }}
          data-item-id={`ticket-${ticket.id}`}
          title={`${ticket.title}\nTicket: ${ticket.ticketNumber}\n${formatTicketTime(ticket.plannedStartDateTime)} - ${formatTicketTime(ticket.plannedEndDateTime)}\nPriorität: ${ticket.priority}\n\nTipp: Ziehen Sie an den Rändern, um die Dauer zu ändern`}
          draggable={!resizeMode && !operationInProgress && !ticket.isArchived}
          onDragStart={(e) => {
            if (ticket.isArchived) { e.preventDefault(); return; }
            handlePlannedTicketDragStart(e, ticket);
          }}
          onDragEnd={handlePlannedTicketDragEnd}
          onClick={(e) => handleTicketClick(e, ticket)}
          onContextMenu={(e) => { if (ticket.isArchived) { e.preventDefault(); e.stopPropagation(); return; } handleTicketRightClick(e, ticket); }}
        >
          {/* #604: Kopfzeile -- Marker, Teams, Prioritätspunkt ganz rechts */}
          <KalenderTicketKennung ticket={ticket} />
          
          {/* Resize handle top */}
          <div
            className="resize-handle resize-handle-top"
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '5px',
              cursor: 'n-resize',
              backgroundColor: 'var(--color-glass-10)',
              zIndex: 30,
              borderTop: '1px solid var(--color-glass-30)',
              transition: 'all 0.2s ease'
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              if (ticket.isArchived) return; 
              handleResizeStart(e, ticket, 'top');
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-glass-90)';
              e.target.style.borderTop = '3px solid var(--color-border-inverse)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-glass-10)';
              e.target.style.borderTop = '1px solid var(--color-glass-30)';
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
            }}
            onDragStart={(e) => e.preventDefault()}
            draggable={false}
          />
          
          <CalendarTicketTitle
            ticket={ticket}
            titleStyle={{
              fontWeight: 'bold',
              fontSize: '10px',
              marginTop: '4px',
              marginBottom: '2px',
              pointerEvents: 'none', // Verhindert Event-Interferenz
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere'
            }}
          />
          <div style={{ 
            fontSize: '8px', 
            opacity: 0.9,
            pointerEvents: 'none' // Verhindert Event-Interferenz
          }}>
            {formatTicketTime(ticket.plannedStartDateTime)}
          </div>
          {height > 15 && (
            <div style={{ 
              fontSize: '8px', 
              opacity: 0.8, 
              marginTop: '1px',
              marginBottom: '4px',
              pointerEvents: 'none' // Verhindert Event-Interferenz
            }}>
              #{ticket.ticketNumber}
            </div>
          )}
          
          {/* Resize handle bottom */}
          <div
            className="resize-handle resize-handle-bottom"
            style={{
              position: 'absolute',
              bottom: '0',
              left: '0',
              right: '0',
              height: '5px',
              cursor: 's-resize',
              backgroundColor: 'var(--color-glass-10)',
              zIndex: 30,
              borderBottom: '1px solid var(--color-glass-30)',
              transition: 'all 0.2s ease'
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              if (ticket.isArchived) return;
              handleResizeStart(e, ticket, 'bottom');
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-glass-90)';
              e.target.style.borderBottom = '3px solid var(--color-border-inverse)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-glass-10)';
              e.target.style.borderBottom = '1px solid var(--color-glass-30)';
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
            }}
            onDragStart={(e) => e.preventDefault()}
            draggable={false}
          />
        </div>
      );
    }
  };








  // Live Drag Preview berechnen
  const calculateDragPreview = (e, day) => {
    if (!draggedTicket && !draggedPlannedTicket) return null;
    
    const dayColumn = e.currentTarget.querySelector('.core-hours-section');
    if (!dayColumn) return null;
    
    const rect = dayColumn.getBoundingClientRect();
    
    // Berechne Position basierend auf Oberkante des Elements, nicht auf Mausposition
    let relativeY = e.clientY - rect.top;
    
    // Für geplante Tickets: Verwende den Offset zur Oberkante
    if (draggedPlannedTicket && dragStartPos) {
      relativeY = e.clientY - rect.top - dragStartPos.y;
    }
    
    const topPercent = Math.max(0, Math.min(100, (relativeY / rect.height) * 100));
    
    // Konvertiere zu Minuten (dynamic working day)
    const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
    const totalMinutes = (topPercent / 100) * workingDayMinutes;
    const snappedMinutes = snapToGrid(totalMinutes);
    
    // Berechne neue Startzeit
    const newStartHour = Math.floor(snappedMinutes / 60) + timeStartHour;
    const newStartMinute = snappedMinutes % 60;
    
    const newStartTime = `${newStartHour.toString().padStart(2, '0')}:${newStartMinute.toString().padStart(2, '0')}`;
    
    // Ghost-Höhe anhand geplanter Stunden oder 60 Minuten
    let durationMinutes = 60;
    if (draggedTicket && draggedTicket.plannedHours) {
      const ph = parseFloat(draggedTicket.plannedHours);
      if (!Number.isNaN(ph) && ph > 0) {
        durationMinutes = Math.round(ph * 60);
      }
    } else if (draggedPlannedTicket) {
      try {
        const s = new Date(draggedPlannedTicket.plannedStartDateTime);
        const en = new Date(draggedPlannedTicket.plannedEndDateTime);
        const diff = Math.max(0, (en - s) / (1000 * 60));
        if (diff > 0) durationMinutes = diff;
      } catch (_) {}
    }
    const heightPercent = Math.max(2, Math.min(100, (durationMinutes / workingDayMinutes) * 100));
    
    return {
      day: day.toLocaleDateString('de-DE', { 
        weekday: 'short', 
        day: '2-digit', 
        month: '2-digit' 
      }),
      startTime: newStartTime,
      position: { x: e.clientX, y: e.clientY },
      topPercent,
      heightPercent
    };
  };

  // Drag & Drop Funktionen
  const handleDragOver = (e, day) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(day);
    
    // Berechne und zeige Live-Feedback
    const preview = calculateDragPreview(e, day);
    if (preview) {
      setDragPreview(preview);
      setShowDragPreview(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverDay(null);
    setShowDragPreview(false);
  };





  // ===== TICKET DETAIL HANDLERS =====
  const handleEventClick = async (e, event) => {
    setSelectedEvent(event);
    setShowEventDetail(true);
  };
  const handleTicketClick = async (e, ticket) => {
    // Don't open modal if we just finished resizing
    if (justResized) {
      setJustResized(false);
      return;
    }
    
    // Don't open modal if clicking on resize handles
    if (e.target.classList.contains('resize-handle')) {
      return;
    }
    
    // Optimistisch: Entferne die rote Ecke sofort (verwende originalTicketId für mehrtägige Termine)
    const ticketId = ticket.originalTicketId || ticket.id;
    setPlannedTickets(currentTickets => currentTickets.map(t => 
      (t.originalTicketId || t.id) === ticketId ? { ...t, hasNewEmails: false, hasNewComments: false, hasNewAttachments: false, isNewTicket: false, hasNewActivity: false } : t
    ));
    
    // Öffne das Modal sofort
    setSelectedTicket(ticket);
    setShowTicketDetail(true);
    
    // Mark ticket as viewed im Hintergrund (verwende originalTicketId für mehrtägige Termine)
    try {
      await apiService.markTicketAsViewed(ticketId);
    } catch (error) {
      console.error('Error marking ticket as viewed:', error);
      
      // Falls der API-Call fehlschlägt, zeige die rote Ecke wieder an
      setPlannedTickets(currentTickets => currentTickets.map(t => 
        (t.originalTicketId || t.id) === ticketId ? { ...t, hasNewActivity: true } : t
      ));
    }
  };

  const handleTicketDetailClose = async (hasChanges = false) => {
    setShowTicketDetail(false);
    const ticketId = selectedTicket?.id;
    setSelectedTicket(null);
    
    // Handle different types of changes
    if (hasChanges === 'status_uncompleted') {
      console.log('📅 [CALENDAR] Ticket status changed from abgeschlossen, adding back to sidebar...');
      
      // Dynamically add ticket back to sidebar using special function for completed->other status changes
      if (removeTicketFromSidebar.current?.addTicketBackFromCompleted && ticketId) {
        await removeTicketFromSidebar.current.addTicketBackFromCompleted(ticketId);
        console.log('📅 [CALENDAR] Dynamically added uncompleted ticket back to sidebar');
      }
      
      // Refresh calendar to update ticket status in planning
      await loadCalendarDataSilently();
    } else if (hasChanges === 'completed') {
      console.log('📅 [CALENDAR] Ticket was completed, refreshing calendar...');
      // Entferne sofort aus Sidebar (falls vorhanden)
      try {
        if (removeTicketFromSidebar.current?.removeTicket && ticketId) {
          removeTicketFromSidebar.current.removeTicket(ticketId);
          console.log('📅 [CALENDAR] Removed completed ticket from sidebar');
        }
      } catch (_) {}
      await loadCalendarDataSilently();
  } else if (hasChanges === 'transferred') {
    console.log('📅 [CALENDAR] Ticket was transferred, refreshing calendar...');
    await loadCalendarDataSilently();
    } else if (hasChanges === true) {
      console.log('📅 [CALENDAR] Ticket was modified, performing dynamic update...');
      await handleTicketUpdate();
    } else {
      console.log('📅 [CALENDAR] Ticket was only viewed, no refresh needed');
    }
  };

  const handleTicketUpdate = useCallback(async (ticketId = null) => {
    // Silent calendar refresh to update modified ticket in calendar immediately
    await loadCalendarDataSilently();
    
    // If we have a specific ticket ID or a selected ticket, update it with the latest data
    const targetTicketId = ticketId || selectedTicket?.id;
    if (targetTicketId) {
      try {
        const response = await apiService.getTickets();
        const updatedTicket = response.tickets.find(t => t.id === targetTicketId);
        if (updatedTicket) {
          setSelectedTicket(updatedTicket);
        }
      } catch (error) {
        console.error('Error updating selected ticket:', error);
      }
    }
  }, [selectedTicket, loadCalendarDataSilently]);

  // Handle ticket creation
  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (isCreatingTicket) return;
    
    if (!newTicket.title.trim()) {
      flagInvalidField(e.currentTarget, '#ticket-title', t('dashboard.enterTitle', 'Bitte geben Sie einen Titel ein.')) || pushToast({ type: 'error', message: t('dashboard.enterTitle', 'Bitte geben Sie einen Titel ein.') });
      return;
    }

    try {
      setIsCreatingTicket(true);
      // Create ticket and get the response
      const response = await apiService.createTicket(newTicket);
      const createdTicket = response.ticket || response;

      // If created via selection, immediately plan into current user's calendar
      if (pendingPlannedTimeRange) {
        const planningData = {
          startDateTime: pendingPlannedTimeRange.startDateTime,
          endDateTime: pendingPlannedTimeRange.endDateTime
        };
        if (currentUser) {
          planningData.assignedTo = currentUser.mail || currentUser.userPrincipalName;
        }

        try {
          await apiService.planTicket(createdTicket.id, planningData);
          // Clear selection state and refresh calendar
          setPendingPlannedTimeRange(null);
          setIsSelecting(false);
          setSelectionRect(null);
          setSelectionDay(null);
          await loadCalendarDataSilently();
        } catch (planErr) {
          console.error('Error planning newly created ticket in calendar view:', planErr);
          setError(t('calendar.planNewTicketError', 'Fehler beim Planen des neu erstellten Tickets'));
        }
      } else {
        // Construct the full ticket object for dynamic sidebar update
        const ticketForSidebar = {
          id: createdTicket.id,
          ticketNumber: createdTicket.ticketNumber,
          title: newTicket.title,
          description: newTicket.description || '',
          priority: newTicket.priority || 'normal',
          category: newTicket.category || '',
          assignedTo: '',
          status: 'offen',
          isPlanned: false,
          tags: newTicket.tags || [],
          calenderMarker: newTicket.calenderMarker || null,
          __forceVisible: true,
          hasNewActivity: true,
          isNewTicket: true,
          createdAt: new Date().toISOString(),
          ...createdTicket
        };
        console.log('📅 [CALENDAR] Dynamically adding newly created ticket to sidebar');
        if (removeTicketFromSidebar.current?.addNewTicket) {
          removeTicketFromSidebar.current.addNewTicket(ticketForSidebar);
        }
      }

      // Close modal and reset form
      setShowCreateTicketForm(false);
      setNewTicket({
        title: '',
        description: '',
        priority: 'normal',
        category: '',
        tags: [],
        calenderMarker: null,
        customerId: null,
        contactId: null
      });
    } catch (error) {
      console.error('Error creating ticket:', error);
      pushToast({ type: 'error', message: t('calendar.createTicketError', 'Fehler beim Erstellen des Tickets.') });
    } finally {
      setIsCreatingTicket(false);
    }
  };

  // Handle opening create ticket form
  const handleCreateTicketClick = () => {
    setShowCreateTicketForm(true);
  };

  // Close modal helper (also clears selection planning state)
  const closeCreateTicketModal = () => {
    setShowCreateTicketForm(false);
    setPendingPlannedTimeRange(null);
    setIsSelecting(false);
    setSelectionRect(null);
    setSelectionDay(null);
  };

  // Load available emails for ticket assignment
  const loadAvailableEmails = async () => {
    try {
      await apiService.getAvailableEmails();
    } catch (error) {
      console.error('Error loading available emails:', error);
    }
  };

  // Load available emails when create form opens
  useEffect(() => {
    if (showCreateTicketForm) {
      loadAvailableEmails();
    }
  }, [showCreateTicketForm]);

  // Local create-submit lock state
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);

  // ===== CONTEXT MENU HANDLERS =====
  const handleTicketRightClick = (e, ticket) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      ticket: ticket
    });
  };

  // #119 (W7): auf den ersten freien Arbeitszeit-Slot der nächsten Woche
  // verschieben (Slot-Suche macht der Server, Route /plan-next-week)
  const handleMoveToNextWeek = async () => {
    if (!contextMenu.ticket) return;
    if (operationInProgress) return;
    const ticketId = contextMenu.ticket.originalTicketId || contextMenu.ticket.id;
    setContextMenu({ visible: false, x: 0, y: 0, ticket: null });
    setOperationInProgress(true);
    setOperationMessage(t('contextMenu.movingNextWeek', 'Wird auf nächste Woche verschoben...'));
    try {
      const resp = await apiService.planTicketNextWeek(ticketId);
      const start = resp?.plannedStartDateTime ? new Date(resp.plannedStartDateTime) : null;
      const when = start ? start.toLocaleString([], { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
      pushToast({ message: t('contextMenu.movedNextWeek', `Verschoben auf ${when}`, { date: when }), type: 'success' });
      try {
        const { startOfWeek, endOfWeek } = getCurrentWeekRange();
        await loadCalendarDataForDateRange(startOfWeek, endOfWeek);
      } catch (_) {}
    } catch (error) {
      console.error('Error planning ticket for next week:', error);
      const serverMsg = error?.response?.data?.error;
      pushToast({ message: serverMsg || t('contextMenu.moveNextWeekError', 'Verschieben auf nächste Woche fehlgeschlagen.'), type: 'error' });
    } finally {
      setOperationInProgress(false);
      setOperationMessage('');
    }
  };

  const handleRemoveFromPlanning = async () => {
    if (!contextMenu.ticket) return;
    
    // Block if another operation is in progress
    if (operationInProgress) {
      console.log('🚫 Remove from planning blocked - operation in progress:', operationMessage);
      return;
    }
    
    setOperationInProgress(true);
    setOperationMessage(t('global.removingFromPlanning', 'Ticket wird aus Planung entfernt...'));
    
    try {
      const ticketId = contextMenu.ticket.originalTicketId || contextMenu.ticket.id;
      await apiService.unplanTicket(ticketId);
      
      // Dynamically add ticket back to sidebar for immediate UI feedback (force visible)
      if (removeTicketFromSidebar.current?.addNewTicket) {
        const forcedTicket = {
          ...(contextMenu.ticket || {}),
          id: ticketId,
          isPlanned: false,
          __forceVisible: true,
          hasNewActivity: true,
          isNewTicket: true,
          createdAt: new Date().toISOString()
        };
        removeTicketFromSidebar.current.addNewTicket(forcedTicket);
        console.log('📅 [CALENDAR] Dynamically added ticket back to sidebar (force-visible)');
      } else if (removeTicketFromSidebar.current?.addTicket) {
        await removeTicketFromSidebar.current.addTicket(ticketId);
        console.log('📅 [CALENDAR] Dynamically added ticket back to sidebar (fallback)');
      }
      
      // Schließe Kontextmenü
      setContextMenu({ visible: false, x: 0, y: 0, ticket: null });
      
      // Silent calendar refresh to remove ticket from calendar immediately
      await loadCalendarDataSilently();
      
      // Zusätzliche Erfolgsmeldung für den Benutzer
      console.log(`✅ Ticket ${contextMenu.ticket.ticketNumber} wurde aus der Planung entfernt. Die Zuordnung bleibt unverändert.`);
      
    } catch (error) {
      console.error('Error removing ticket from planning:', error);
      setError(t('calendar.removePlanningError', 'Fehler beim Entfernen des Tickets aus der Planung'));
    } finally {
      setOperationInProgress(false);
      setOperationMessage('');
    }
  };



  

  // ===== ORPHANED TICKET REPAIR =====
  // The repair functionality is now integrated into the automatic synchronization process
  // and runs automatically every 60 seconds in the background

  // ===== DRAG & DROP FÜR GEPLANTE TICKETS =====
  const handlePlannedTicketDragStart = (e, ticket) => {
    // Verhindere Drag-Start, wenn wir im Resize-Modus sind oder auf einem Resize-Handle geklickt wird
    if (resizeMode || e.target.classList.contains('resize-handle')) {
      e.preventDefault();
      return;
    }
    
    // Block if another operation is in progress
    if (operationInProgress) {
      e.preventDefault();
      console.log('🚫 Planned ticket drag blocked - operation in progress:', operationMessage);
      return;
    }
    
    setDraggedPlannedTicket(ticket);
    
    // Speichere die Maus-Position relativ zum Element
    const rect = e.currentTarget.getBoundingClientRect();
    setDragStartPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({
      type: 'planned-ticket',
      ticket: ticket
    }));
  };

  const handlePlannedTicketDragEnd = () => {
    setDraggedPlannedTicket(null);
    setDragStartPos(null);
    setShowDragPreview(false);
  };

  // Resize Funktionen
  const handleResizeStart = (e, ticket, handle) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Block if another operation is in progress
    if (operationInProgress) {
      console.log('🚫 Resize blocked - operation in progress:', operationMessage);
      return;
    }
    
    // Clean up any existing listeners, but don't reset the state yet
    cleanupResizeListeners();
    

    
    // Aktiviere Resize-Modus
    setResizeMode(true);
    
    setResizingTicket(ticket);
    setResizeHandle(handle);
    const startPos = { x: e.clientX, y: e.clientY };
    setResizeStartPos(startPos);
    setResizeStartHeight(e.currentTarget.parentElement.offsetHeight);
    setShowResizePreview(true);
    setJustResized(false);
    
    // Initialize resize values with current ticket values
    currentResizeValues.current = {
      startDateTime: ticket.plannedStartDateTime,
      endDateTime: ticket.plannedEndDateTime
    };
    
    // Create bound functions for event listeners with ticket and handle in closure
    const boundHandleResizeMove = (e) => {
      handleResizeMoveWithTicket(e, ticket, handle, startPos);
    };
    const boundHandleResizeEnd = (e) => {
      handleResizeEndWithTicket(e, ticket, handle);
    };
    
    // Store references for cleanup
    resizeMouseMoveRef.current = boundHandleResizeMove;
    resizeMouseUpRef.current = boundHandleResizeEnd;
    
    // Add event listeners
    document.addEventListener('mousemove', boundHandleResizeMove, { passive: false });
    document.addEventListener('mouseup', boundHandleResizeEnd);
  };

  const handleResizeMoveWithTicket = (e, ticket, handle, startPos) => {
    if (!ticket || !handle) return;
    
    e.preventDefault();
    
    const deltaY = e.clientY - startPos.y;
    const workingDayMinutes = 10 * 60; // 10 hours from 8 to 18
    
    // Get actual height of the core-hours-section dynamically
    const coreHoursSection = document.querySelector('.core-hours-section');
    const coreHoursHeight = coreHoursSection ? coreHoursSection.offsetHeight : 600;
    
    const minutesPerPixel = workingDayMinutes / coreHoursHeight;
    const deltaMinutes = deltaY * minutesPerPixel;
    
    const originalStart = new Date(ticket.plannedStartDateTime);
    const originalEnd = new Date(ticket.plannedEndDateTime);
    
    let newStartDateTime, newEndDateTime;
    
    if (handle === 'top') {
      // Resizing from top - change start time with 30-minute snapping
      const newStartMinutes = originalStart.getHours() * 60 + originalStart.getMinutes() + deltaMinutes;
      const snappedStartMinutes = snapToGrid(newStartMinutes);
      
      newStartDateTime = new Date(originalStart);
      newStartDateTime.setHours(Math.floor(snappedStartMinutes / 60), snappedStartMinutes % 60, 0, 0);
      newEndDateTime = originalEnd;
    } else if (handle === 'bottom') {
      // Resizing from bottom - change end time with 30-minute snapping
      const newEndMinutes = originalEnd.getHours() * 60 + originalEnd.getMinutes() + deltaMinutes;
      const snappedEndMinutes = snapToGrid(newEndMinutes);
      
      newStartDateTime = originalStart;
      newEndDateTime = new Date(originalEnd);
      newEndDateTime.setHours(Math.floor(snappedEndMinutes / 60), snappedEndMinutes % 60, 0, 0);
    }
    
    // Minimum duration: 30 minutes
    const minDuration = 30 * 60 * 1000;
    if (newEndDateTime - newStartDateTime < minDuration) {
      return;
    }
    
    // Store current resize values in ref
    currentResizeValues.current = {
      startDateTime: newStartDateTime.toISOString(),
      endDateTime: newEndDateTime.toISOString()
    };
    
    // Update resize preview
    const userTimeZone = getUserTimeZone();
    const startTime = newStartDateTime.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimeZone
    });
    const endTime = newEndDateTime.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: userTimeZone
    });
    
    setResizePreview({
      startTime,
      endTime,
      position: { x: e.clientX, y: e.clientY }
    });
    
    // Update the ticket temporarily for visual feedback
    const updatedTicket = {
      ...ticket,
      plannedStartDateTime: newStartDateTime.toISOString(),
      plannedEndDateTime: newEndDateTime.toISOString()
    };
    
    // Update the planned tickets array for immediate visual feedback
    setPlannedTickets(prev => 
      prev.map(t => t.id === ticket.id ? updatedTicket : t)
    );
  };

  const handleResizeEndWithTicket = async (e, ticket, handle) => {
    if (!ticket) {
      // Cleanup listeners even if no ticket is being resized
      cleanupResizeListeners();
      return;
    }
    
    // Cleanup event listeners first
    cleanupResizeListeners();
    
    setShowResizePreview(false);
    setResizePreview(null);
    
    // Set flag to prevent click event after resize
    setJustResized(true);
    
    // Reset flag after a short delay to allow normal clicks again
    setTimeout(() => {
      setJustResized(false);
      setResizeMode(false); // Deaktiviere Resize-Modus
    }, 150);
    
    setOperationInProgress(true);
    setOperationMessage(t('global.changingTicketDuration', 'Ticket-Dauer wird geändert...'));
    
    try {
      // Check if we have valid resize values from the ref
      if (!currentResizeValues.current.startDateTime || !currentResizeValues.current.endDateTime) {
        return;
      }
      
      const newStartDateTime = new Date(currentResizeValues.current.startDateTime);
      const newEndDateTime = new Date(currentResizeValues.current.endDateTime);
      
      // Minimum duration: 30 minutes
      const minDuration = 30 * 60 * 1000;
      if (newEndDateTime - newStartDateTime < minDuration) {
        return;
      }
      
      // Update ticket planning with Microsoft Graph integration
      const planningUpdateData = {
        startDateTime: currentResizeValues.current.startDateTime,
        endDateTime: currentResizeValues.current.endDateTime,
        isPlanned: true
      };
      
      // Füge planningId hinzu für mehrtägige Termine
      if (ticket.planningId) {
        planningUpdateData.planningId = ticket.planningId;
      }
      
      // Add user assignment if available - always assign to current user when resized
      if (currentUser) {
        planningUpdateData.assignedTo = currentUser.mail || currentUser.userPrincipalName;
      }

      const ticketId = ticket.originalTicketId || ticket.id;
      await apiService.updateTicketPlanning(ticketId, planningUpdateData);
      
      // Silent calendar refresh to show updated ticket timing immediately
      await loadCalendarDataSilently();
      
    } catch (error) {
      console.error('Error updating ticket planning during resize:', error);
      setError(t('calendar.changeDurationError', 'Fehler beim Ändern der Ticket-Dauer'));
      // Silent refresh to restore original state
      await loadCalendarDataSilently();
    } finally {
      // Clean up all resize state
      setResizingTicket(null);
      setResizeHandle(null);
      setResizeStartPos({ x: 0, y: 0 });
      setResizeStartHeight(0);
      
      // Reset resize values
      currentResizeValues.current = { startDateTime: null, endDateTime: null };
      
      // Reset operation in progress
      setOperationInProgress(false);
      setOperationMessage('');
    }
  };

  const handleDropOnDay = async (e, day) => {
    e.preventDefault();
    setDragOverDay(null);
    setShowDragPreview(false);
    
    // Block if another operation is in progress
    if (operationInProgress) {
      console.log('🚫 Drop blocked - operation in progress:', operationMessage);
      return;
    }
    
    // Ticket- und Drop-Kontext ermitteln (Position/Zeit wird weiter unten berechnet)
    let dragData;
    try { dragData = JSON.parse(e.dataTransfer.getData('text/plain')); } catch(_) { dragData = null; }

    // Öffne Bestätigungsmodal nur bei geplanten Tickets (Drag aus Kalender)
    if (dragData && dragData.type === 'planned-ticket') {
      try {
        const dayColumn = e.currentTarget.querySelector('.core-hours-section');
        const rect = dayColumn.getBoundingClientRect();
        let relativeY = e.clientY - rect.top;
        if (dragStartPos) relativeY = e.clientY - rect.top - dragStartPos.y;
        const heightPercent = Math.max(0, Math.min(100, (relativeY / rect.height) * 100));
        const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
        const totalMinutes = (heightPercent / 100) * workingDayMinutes;
        const snappedMinutes = snapToGrid(totalMinutes);
        const newStartHour = Math.floor(snappedMinutes / 60) + timeStartHour;
        const newStartMinute = snappedMinutes % 60;
        const newStartDateTime = new Date(day);
        newStartDateTime.setHours(newStartHour, newStartMinute, 0, 0);
        setDropChoiceModal({ visible: true, context: { source: 'calendar', ticket: dragData.ticket, targetDay: new Date(day), newStartDateTime, origin: (dragData && dragData.origin) || null } });
        return;
      } catch (_) {
        // Falls Berechnung fehlschlägt, fallback auf Standard-Flow (verschieben)
      }
    }

    setOperationInProgress(true);
    setOperationMessage(t('global.movingTicket', 'Ticket wird verschoben...'));
    
    try {
      JSON.parse(e.dataTransfer.getData('text/plain')); // Validierung: wirft bei fremden Drop-Daten (Ergebnis wird nicht mehr gelesen)
      
      // Berechne Drop-Position für beide Ticket-Typen
      const dayColumn = e.currentTarget.querySelector('.core-hours-section');
      const rect = dayColumn.getBoundingClientRect();
      
      // Berechne Position basierend auf Oberkante des Elements, nicht auf Mausposition
      let relativeY = e.clientY - rect.top;
      
      // Für geplante Tickets: Verwende den Offset zur Oberkante
      if (dragData.type === 'planned-ticket' && dragStartPos) {
        relativeY = e.clientY - rect.top - dragStartPos.y;
      }
      
      const heightPercent = Math.max(0, Math.min(100, (relativeY / rect.height) * 100));
      
    // Konvertiere zu Minuten (dynamic working day)
    const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
      const totalMinutes = (heightPercent / 100) * workingDayMinutes;
      const snappedMinutes = snapToGrid(totalMinutes);
      
      // Berechne neue Startzeit
    const newStartHour = Math.floor(snappedMinutes / 60) + timeStartHour;
      const newStartMinute = snappedMinutes % 60;
      
      if (dragData.type === 'planned-ticket') {
        // Geplantes Ticket verschieben
        const ticket = dragData.ticket;
        
        // Berechne Dauer des ursprünglichen Tickets
        const originalStart = new Date(ticket.plannedStartDateTime);
        const originalEnd = new Date(ticket.plannedEndDateTime);
        const duration = originalEnd - originalStart;
        
        // Setze neue Zeiten
        const newStartDateTime = new Date(day);
        newStartDateTime.setHours(newStartHour, newStartMinute, 0, 0);
        
        const newEndDateTime = new Date(newStartDateTime.getTime() + duration);
        
        // Update ticket planning and assign to current user if not assigned
        const planningUpdateData = {
          startDateTime: newStartDateTime.toISOString(),
          endDateTime: newEndDateTime.toISOString(),
          isPlanned: true
        };
        
        // Add user assignment if available - always assign to current user when moved to calendar
        if (currentUser) {
          planningUpdateData.assignedTo = currentUser.mail || currentUser.userPrincipalName;
        }

        await apiService.updateTicketPlanning(ticket.id, planningUpdateData);

        // Silent calendar refresh to show updated ticket position immediately
        await loadCalendarDataSilently();

        // Remove from sidebar immediately if this planned ticket was dragged from the sidebar (overdue case)
        try {
          if (dragData && dragData.origin === 'sidebar' && removeTicketFromSidebar.current?.removeTicket) {
            removeTicketFromSidebar.current.removeTicket(ticket.id);
            console.log('📅 [CALENDAR] Removed moved planned ticket from sidebar (origin sidebar)');
          }
        } catch (_) {}
        
      } else if (dragData.type === 'ticket') {
        // Ungeplantes Ticket direkt platzieren (kein Modal!)
        const ticket = dragData.ticket;
        
        // Dauer: geplante Stunden falls vorhanden, sonst 1 Stunde
        const ph = parseFloat(ticket?.plannedHours);
        const durationMs = (!Number.isNaN(ph) && ph > 0) ? Math.round(ph * 60 * 60 * 1000) : (60 * 60 * 1000);
        
        // Setze neue Zeiten
        const newStartDateTime = new Date(day);
        newStartDateTime.setHours(newStartHour, newStartMinute, 0, 0);
        
        const newEndDateTime = new Date(newStartDateTime.getTime() + durationMs);
        
        // Plan ticket with calendar and assign to current user
        const planningData = {
          startDateTime: newStartDateTime.toISOString(),
          endDateTime: newEndDateTime.toISOString()
        };
        
        // Add user assignment if available
        if (currentUser) {
          planningData.assignedTo = currentUser.mail || currentUser.userPrincipalName;
        }

        await apiService.planTicket(ticket.id, planningData);

        // Dynamically remove ticket from sidebar for immediate UI feedback
        if (removeTicketFromSidebar.current?.removeTicket) {
          removeTicketFromSidebar.current.removeTicket(ticket.id);
          console.log('📅 [CALENDAR] Dynamically removed ticket from sidebar');
        }

        // Silent calendar refresh to show ticket in calendar immediately
        await loadCalendarDataSilently();
      }
    } catch (error) {
      console.error('Error handling drop:', error);
      setError(t('calendar.placeTicketError', 'Fehler beim Platzieren des Tickets'));
    } finally {
      setOperationInProgress(false);
      setOperationMessage('');
    }
  };



  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <RefreshCw className="animate-spin" size={32} />
          <p>{t('calendar.loading', 'Lade Termine...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const timeSlots = getTimeSlots();

  return (
    <div className="container">
      <div className="calendar-with-sidebar">
        <TicketSidebar 
          isLoading={loading}
          onRefresh={handleSidebarOnlyRefresh}
          onTicketDragStart={(ticket) => {
            // Block sidebar ticket drag start if operation is in progress
            if (operationInProgress) {
              console.log('🚫 Sidebar drag blocked - operation in progress:', operationMessage);
              return false; // Return false to signal blocking
            }
            setDraggedTicket(ticket);
            return true; // Return true to signal success
          }}
          onTicketDragEnd={() => {
            setDraggedTicket(null);
            setShowDragPreview(false);
          }}
          onCreateTicket={handleCreateTicketClick}
          onTicketClick={handleTicketClick}
          onTicketPlanned={handleTicketPlannedCallback}
        />
        
        <div className="calendar-main">
          <div className="calendar-view" style={{ '--time-col-width': '80px' }}>
            <div className="calendar-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxHeight: '35px' }}>
              <h2 style={{ flex: '0 0 auto' }}>
                <Calendar size={24} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
                KW{getWeekNumber(weekStart)}
              </h2>
              <div className="calendar-actions" style={{ flex: '1 1 auto', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <div className="sidebar-search-container" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div className="search-input-wrapper">
                    <Search size={16} className="search-icon" />
                    <input
                      type="text"
                      placeholder={t('global.searchInCalendar', 'Im Kalender suchen...')}
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); performSearch(e.target.value); }}
                      className="sidebar-search-input"
                    />
                  </div>
                  <button
                    onClick={goToPrevSearchResult}
                    className="btn btn-secondary icon-only"
                    disabled={!searchResults || searchResults.length === 0 || currentSearchIndex <= 0}
                    title={t('global.prevResult', 'Vorheriges Ergebnis')} aria-label={t('global.prevResult', 'Vorheriges Ergebnis')}
                    style={{ padding: '2px', minWidth: 'auto', width: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    onClick={goToNextSearchResult}
                    className="btn btn-secondary icon-only"
                    disabled={!searchResults || searchResults.length === 0 || currentSearchIndex >= searchResults.length - 1}
                    title={t('global.nextResult', 'Nächstes Ergebnis')} aria-label={t('global.nextResult', 'Nächstes Ergebnis')}
                    style={{ padding: '2px', minWidth: 'auto', width: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
                <button 
                  onClick={() => navigateWeek(-1)}
                  className="btn btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <ChevronLeft size={16} />
                  {t('calendar.previousWeek', 'Vorherige Woche')}
                </button>
                
                <button 
                  onClick={goToToday}
                  className="btn btn-primary"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  disabled={isCurrentWeek()}
                >
                  <Target size={16} />
                  {t('calendar.today', 'Heute')}
                </button>
                
                <button 
                  onClick={() => navigateWeek(1)}
                  className="btn btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  {t('calendar.nextWeek', 'Nächste Woche')}
                  <ChevronRight size={16} />
                </button>
                
                <button 
                  onClick={() => setShowExtendedWeek(!showExtendedWeek)}
                  className="btn btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                  title={showExtendedWeek ? t('calendar.backToWeekTooltip', 'Zur Wochenansicht zurückkehren') : t('calendar.show14DaysTooltip', '14 Tage anzeigen')}
                >
                  <Calendar size={16} />
                  {showExtendedWeek ? t('calendar.week', 'Woche') : t('calendar.14days', '14 Tage')}
                </button>
              </div>
            </div>

            <div className="calendar-grid">
              <div 
                className="calendar-week-header" 
                style={{ 
                  display: 'grid', 
                  gridTemplateColumns: `var(--time-col-width) repeat(${visibleDays.length}, 1fr)`,
                  gap: '1px'
                }}
              >
                <div className="time-column-header">Zeit</div>
                {visibleDays.map((day, index) => (
                  <div 
                    key={index} 
                    className={`day-header ${isToday(day) ? 'today' : ''}`}
                    style={{
                      minWidth: 0,
                      ...(showExtendedWeek && {
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        minHeight: '80px'
                      })
                    }}
                  >
                    <div className="day-name">
                      {day.toLocaleDateString('de-DE', { weekday: 'short', timeZone: getUserTimeZone() })}
                    </div>
                    <div className="day-date">
                      {day.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', timeZone: getUserTimeZone() })}
                    </div>
                  </div>
                ))}
              </div>

              <div 
                className="calendar-week-body" 
                style={{ 
                  display: 'grid', 
                  gridTemplateColumns: `var(--time-col-width) repeat(${visibleDays.length}, 1fr)`,
                  gap: '1px'
                }}
              >
                <div className="time-column" style={{ width: 'var(--time-col-width)' }}>
                  {timeSlots.map(hour => (
                    <div
                      key={hour}
                      className="time-slot"
                      style={{ height: `${100 / (timeEndHour - timeStartHour)}%` }}
                    >
                      {hour.toString().padStart(2, '0')}:00
                    </div>
                  ))}
                </div>

                {visibleDays.map((day, dayIndex) => {
                  const { items: positionedItems, hasAbove, hasBelow } =
                    tagesZellen.get(day.getTime()) || LEERE_ZELLE;
                  const isDragOver = dragOverDay && dragOverDay.toDateString() === day.toDateString();
                  

                  
                  return (
                    <div 
                      key={dayIndex} 
                      className={`day-column drop-zone ${isDragOver ? 'drag-over' : ''} ${operationInProgress ? 'operation-blocked' : ''}`}
                      style={{ position: 'relative' }}
                                              onDragOver={(e) => handleDragOver(e, day)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDropOnDay(e, day)}
                        title={operationInProgress ? `Warten Sie: ${operationMessage}` : ''}
                    >
                      {/* Above indicator */}
                      <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // toggle: collapse back to default when already expanded
                            if (timeStartHour <= 1) {
                              setTimeStartHour(8);
                            } else if (hasAbove) {
                              setTimeStartHour(0);
                            } else {
                              setTimeStartHour(1);
                            }
                          }}
                          className="btn btn-xxs"
                          title={hasAbove ? 'Termine über 8 Uhr anzeigen' : 'Bis 1 Uhr erweitern'}
                          style={{
                            padding: '0 6px',
                            fontSize: '10px',
                            lineHeight: '12px',
                            border: 'none',
                            background: 'transparent',
                            color: hasAbove ? 'var(--color-danger)' : 'var(--color-text-faint)',
                            cursor: 'pointer',
                            position: 'absolute',
                            top: '0',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 900,
                            pointerEvents: 'auto'
                          }}
                        >
                          ▲
                        </button>
                      {/* Core hours section (all hours) */}
                      <div className="core-hours-section">
                        <div className="day-events" onMouseDown={(e) => {
                          // Only left-click, block during operations, ignore when clicking on items
                          if (operationInProgress || e.button !== 0) return;
                          if (e.target.closest && (
                            e.target.closest('.calendar-ticket') ||
                            e.target.closest('.calendar-event') ||
                            e.target.closest('.resize-handle')
                          )) return;

                          const section = e.currentTarget;
                          const rect = section.getBoundingClientRect();

                          setIsSelecting(true);
                          setSelectionDay(day);
                          selectionContainerRectRef.current = rect;
                          selectionStartYRef.current = e.clientY - rect.top;
                          setSelectionRect({ topPercent: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)), heightPercent: 0 });

                          const moveHandler = (moveEvent) => {
                            if (!selectionContainerRectRef.current) return;
                            moveEvent.preventDefault();
                            const moveRect = selectionContainerRectRef.current;
                            const currentY = moveEvent.clientY - moveRect.top;
                            const startY = selectionStartYRef.current;
                            const top = Math.max(0, Math.min(startY, currentY));
                            const bottom = Math.max(startY, currentY);
                            const topPercent = Math.max(0, Math.min(100, (top / moveRect.height) * 100));
                            const heightPercent = Math.max(0, Math.min(100, ((bottom - top) / moveRect.height) * 100));
                            setSelectionRect({ topPercent, heightPercent });
                          };

                          const upHandler = (upEvent) => {
                            if (!selectionContainerRectRef.current) return;
                            document.removeEventListener('mousemove', selectionMouseMoveRef.current);
                            document.removeEventListener('mouseup', selectionMouseUpRef.current);
                            selectionMouseMoveRef.current = null;
                            selectionMouseUpRef.current = null;

                            const finRect = selectionContainerRectRef.current;
                            const endY = upEvent.clientY - finRect.top;
                            const startY = selectionStartYRef.current;
                            const topPx = Math.max(0, Math.min(startY, endY));
                            const bottomPx = Math.max(startY, endY);

                            const workingDayMinutes = (timeEndHour - timeStartHour) * 60;
                            const startPercent = Math.max(0, Math.min(100, (topPx / finRect.height) * 100));
                            const endPercent = Math.max(0, Math.min(100, (bottomPx / finRect.height) * 100));
                            let startTotalMinutes = (startPercent / 100) * workingDayMinutes;
                            let endTotalMinutes = (endPercent / 100) * workingDayMinutes;

                            // snap to 30 minutes
                            startTotalMinutes = snapToGrid(startTotalMinutes);
                            endTotalMinutes = snapToGrid(endTotalMinutes);

                            // Ensure minimum duration 30 minutes
                            if (endTotalMinutes <= startTotalMinutes) {
                              endTotalMinutes = startTotalMinutes + 30;
                            }

                            const startHour = Math.floor(startTotalMinutes / 60) + timeStartHour;
                            const startMinute = startTotalMinutes % 60;
                            const endHour = Math.floor(endTotalMinutes / 60) + timeStartHour;
                            const endMinute = endTotalMinutes % 60;

                            const startDateTime = new Date(day);
                            startDateTime.setHours(startHour, startMinute, 0, 0);
                            const endDateTime = new Date(day);
                            endDateTime.setHours(endHour, endMinute, 0, 0);

                            setIsSelecting(false);
                            setSelectionRect(null);
                            selectionContainerRectRef.current = null;

                            setPendingPlannedTimeRange({
                              startDateTime: startDateTime.toISOString(),
                              endDateTime: endDateTime.toISOString()
                            });

                            // open create ticket modal
                            setShowCreateTicketForm(true);
                          };

                          selectionMouseMoveRef.current = moveHandler;
                          selectionMouseUpRef.current = upHandler;
                          document.addEventListener('mousemove', moveHandler, { passive: false });
                          document.addEventListener('mouseup', upHandler);
                        }}>
                          {positionedItems.map((item, index) => renderCalendarItem(item, index))}
                          {/* Ghost-Vorschau für neue Tickets aus der Sidebar */}
                          {draggedTicket && isDragOver && showDragPreview && dragPreview && typeof dragPreview.topPercent === 'number' && (
                            <div
                              className="calendar-event calendar-event-positioned calendar-ticket ghost"
                              style={{
                                position: 'absolute',
                                top: `${dragPreview.topPercent}%`,
                                left: 2,
                                width: 'calc(100% - 4px)',
                                height: `${dragPreview.heightPercent}%`,
                                backgroundColor: getTicketColor(draggedTicket),
                                color: 'white',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontSize: '10px',
                                lineHeight: '1.1',
                                border: '2px dashed var(--color-glass-90)',
                                opacity: 0.7,
                                zIndex: 14,
                                pointerEvents: 'none',
                                display: 'flex',
                                flexDirection: 'column'
                              }}
                              title={`${draggedTicket.title}\n${dragPreview.startTime}`}
                            >
                              <div style={{ fontWeight: 'bold', fontSize: '10px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                {draggedTicket.title}
                              </div>
                              <div style={{ fontSize: '8px', opacity: 0.9 }}>{dragPreview.startTime}</div>
                            </div>
                          )}
                          {draggedPlannedTicket && isDragOver && showDragPreview && dragPreview && typeof dragPreview.topPercent === 'number' && (
                            <div
                              className="calendar-event calendar-event-positioned calendar-ticket ghost"
                              style={{
                                position: 'absolute',
                                top: `${dragPreview.topPercent}%`,
                                left: 2,
                                width: 'calc(100% - 4px)',
                                height: `${dragPreview.heightPercent}%`,
                                backgroundColor: getTicketColor(draggedPlannedTicket),
                                color: 'white',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontSize: '10px',
                                lineHeight: '1.1',
                                border: '2px dashed var(--color-glass-90)',
                                opacity: 0.7,
                                zIndex: 14,
                                pointerEvents: 'none',
                                display: 'flex',
                                flexDirection: 'column'
                              }}
                              title={`${draggedPlannedTicket.title}\n${dragPreview.startTime}`}
                            >
                              <div style={{ fontWeight: 'bold', fontSize: '10px', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                {draggedPlannedTicket.title}
                              </div>
                              <div style={{ fontSize: '8px', opacity: 0.9 }}>{dragPreview.startTime}</div>
                            </div>
                          )}
                        </div>

                        {isSelecting && selectionDay && day.toDateString() === new Date(selectionDay).toDateString() && selectionRect && (
                          <div
                            style={{
                              position: 'absolute',
                              left: '2px',
                              right: '2px',
                              top: `${selectionRect.topPercent}%`,
                              height: `${selectionRect.heightPercent}%`,
                              backgroundColor: 'var(--color-cal-auswahl)',
                              border: '2px dashed var(--color-cal-auswahl-rand)',
                              borderRadius: '4px',
                              pointerEvents: 'none',
                              zIndex: 20
                            }}
                          />
                        )}
                        
                        {/* Current time marker */}
                        {isToday(day) && getCurrentTimePosition(currentTime) !== null && (
                          <div 
                            className="current-time-marker"
                            style={{
                              position: 'absolute',
                              top: `${getCurrentTimePosition(currentTime)}%`,
                              left: '0',
                              right: '0',
                              height: '2px',
                              backgroundColor: 'var(--color-danger)',
                              zIndex: 1000,
                              boxShadow: '0 1px 3px var(--color-danger-shadow-30)',
                              borderRadius: '1px'
                            }}
                          >
                            {/* Time indicator dot */}
                            <div
                              style={{
                                position: 'absolute',
                                left: '-6px',
                                top: '-4px',
                                width: '10px',
                                height: '10px',
                                backgroundColor: 'var(--color-danger)',
                                borderRadius: '50%',
                                border: '2px solid white',
                                boxShadow: '0 2px 4px var(--color-shadow-20)'
                              }}
                            />
                            {/* Current time label */}
                            <div
                              style={{
                                position: 'absolute',
                                left: '8px',
                                top: '-12px',
                                backgroundColor: 'var(--color-danger)',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 4px var(--color-shadow-20)',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {currentTime.toLocaleTimeString('de-DE', {
                                hour: '2-digit',
                                minute: '2-digit',
                                timeZone: getUserTimeZone()
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Time grid background */}
                        {timeSlots.map(hour => (
                          <div
                            key={hour}
                            className="time-slot-bg"
                            style={{ height: `${100 / (timeEndHour - timeStartHour)}%` }}
                          ></div>
                        ))}
                      </div>
                      {/* Below indicator */}
                      <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // toggle: collapse back to default when already expanded
                            if (timeEndHour >= 23) {
                              setTimeEndHour(18);
                            } else if (hasBelow) {
                              setTimeEndHour(24);
                            } else {
                              setTimeEndHour(23);
                            }
                          }}
                          className="btn btn-xxs"
                          title={hasBelow ? 'Termine nach 18 Uhr anzeigen' : 'Bis 23 Uhr erweitern'}
                          style={{
                            padding: '0 6px',
                            fontSize: '10px',
                            lineHeight: '12px',
                            border: 'none',
                            background: 'transparent',
                            color: hasBelow ? 'var(--color-danger)' : 'var(--color-text-faint)',
                            cursor: 'pointer',
                            position: 'absolute',
                            bottom: '0',
                            left: '50%',
                            transform: 'translate(-50%, 50%)',
                            zIndex: 900,
                            pointerEvents: 'auto'
                          }}
                        >
                          ▼
                        </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* TICKET DETAIL MODAL */}
      {showTicketDetail && selectedTicket && (
        <TicketDetail 
          isMobileView={isMobileScreen}
          ticket={selectedTicket}
          onClose={handleTicketDetailClose}
          onTicketUpdate={handleTicketUpdate}
          hideCalendarSidebar={true}
        />
      )}

      {/* EVENT DETAIL MODAL */}
      {showEventDetail && selectedEvent && (
        <CalendarEventDetail 
          event={selectedEvent}
          onClose={() => { setShowEventDetail(false); setSelectedEvent(null); }}
        />
      )}

      {/* CREATE TICKET MODAL */}
      {showCreateTicketForm && (
        <Modal
          open
          onClose={closeCreateTicketModal}
          title={<><Ticket size={24} /><h3>{t('dashboard.createTicketHeader', 'Neues Ticket erstellen')}</h3><CalenderMarkerHeaderSelect value={newTicket.calenderMarker} onChange={(value) => setNewTicket({...newTicket, calenderMarker: value })} /></>}
          ariaLabel={t('dashboard.createTicketHeader', 'Neues Ticket erstellen')}
          footer={<>
            <button type="button" onClick={() => setShowCreateTicketForm(false)} className="btn btn-secondary">{t('common.cancel', 'Abbrechen')}</button>
            <button type="submit" form="calendar-create-ticket-form" className="btn btn-primary" disabled={isCreatingTicket}><Plus size={16} />{isCreatingTicket ? t('common.creating', 'Erstelle...') : t('dashboard.createTicket', 'Ticket erstellen')}</button>
          </>}
        >
            
            <form id="calendar-create-ticket-form" onSubmit={handleCreateTicket} className="create-ticket-form">
              <div className="form-body">
                {/* #104: Kunde (halbbreit) über dem Titel */}
                <TicketCustomerField
                  value={newTicket.customerId}
                  onChange={(customerId) => setNewTicket(prev => ({...prev, customerId}))}
                  contactValue={newTicket.contactId}
                  onContactChange={(contactId) => setNewTicket(prev => ({...prev, contactId}))}
                  inputId="ticket-customer"
                />
                <div className="form-group">
                  <label htmlFor="ticket-title">{t('dashboard.field.title', 'Titel')} <span className="required">*</span></label>
                  <input
                    id="ticket-title"
                    type="text"
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                    required
                    placeholder={t('dashboard.placeholder.title', 'Ticket-Titel eingeben...')}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="ticket-description">{t('dashboard.field.description', 'Beschreibung')}</label>
                  <textarea
                    id="ticket-description"
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                    placeholder={t('dashboard.placeholder.description', 'Ticket-Beschreibung eingeben...')}
                    rows="4"
                    className="form-textarea"
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="ticket-priority">{t('dashboard.field.priority', 'Priorität')}</label>
                    <select
                      id="ticket-priority"
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                      className="form-select"
                    >
                      <option value="niedrig">Niedrig</option>
                      <option value="normal">Normal</option>
                      <option value="hoch">Hoch</option>
                      <option value="kritisch">Kritisch</option>
                    </select>
                  </div>
                  
                  
                </div>
                

              </div>
            </form>
        </Modal>
      )}
      
      
      
      {/* LIVE DRAG PREVIEW entfernt */}
      
      {/* LIVE RESIZE PREVIEW */}
      {showResizePreview && resizePreview && (
        <div 
          className="resize-preview"
          style={{
            position: 'fixed',
            left: resizePreview.position.x + 10,
            top: resizePreview.position.y - 40,
            zIndex: 9999,
            background: 'var(--color-cal-resize-hinweis)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px var(--color-shadow-30)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}
        >
          ⏰ {resizePreview.startTime} - {resizePreview.endTime}
        </div>
      )}
      
      {/* CONTEXT MENU */}
      {contextMenu.visible && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 10000,
            background: 'white',
            border: '1px solid var(--color-border-strong)',
            borderRadius: '4px',
            boxShadow: '0 4px 12px var(--color-shadow-20)',
            padding: '8px 0',
            minWidth: '200px',
            fontSize: '14px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          
          <div
            className="context-menu-item"
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              color: 'var(--color-primary)',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onClick={handleMoveToNextWeek}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span>📅</span>
            {t('contextMenu.moveNextWeek', 'Auf nächste Woche verschieben')}
          </div>
          {/* #604 */}
          <GebuchtMenuePunkt ticket={contextMenu.ticket} onClick={() => { const tk = contextMenu.ticket; setContextMenu({ visible: false, x: 0, y: 0, ticket: null }); gebuchtUmschalten(tk); }} />
          <div 
            className="context-menu-item"
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              color: 'var(--color-danger)',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onClick={handleRemoveFromPlanning}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--color-bg)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <span>🗑️</span>
            {t('global.removeFromPlanning', 'Aus Planung entfernen')}
          </div>
          <div style={{ 
            padding: '4px 16px', 
            fontSize: '12px', 
            color: 'var(--color-text-muted)',
            borderTop: '1px solid var(--color-border-faint)',
            marginTop: '4px'
          }}>
            {t('global.ticketLabel', 'Ticket:')} {contextMenu.ticket?.ticketNumber}
          </div>
        </div>
      )}

      {eventContextMenu.visible && (
        <div 
          className="context-menu"
          style={{
            position: 'fixed',
            left: eventContextMenu.x,
            top: eventContextMenu.y,
            zIndex: 10000,
            background: 'white',
            border: '1px solid var(--color-border-strong)',
            borderRadius: '4px',
            boxShadow: '0 4px 12px var(--color-shadow-20)',
            padding: '8px 0',
            minWidth: '200px',
            fontSize: '14px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="context-menu-item"
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              color: 'var(--color-primary)',
              fontWeight: 'bold'
            }}
            onClick={() => {
              const ev = eventContextMenu.event;
              setEventContextMenu({ visible: false, x: 0, y: 0, event: null });
              setSelectedEventForConvert(ev);
              setShowEventConvertModal(true);
            }}
          >
            📌 {t('calendar.convertToTicket', 'In Ticket umwandeln')}
          </div>
          <div 
            className="context-menu-item"
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              color: 'var(--color-danger)',
              fontWeight: 'bold',
              borderTop: '1px solid var(--color-border-faint)'
            }}
            onClick={async () => {
              const ev = eventContextMenu.event;
              setEventContextMenu({ visible: false, x: 0, y: 0, event: null });
              if (await confirm({ message: t('calendar.confirmDeleteEvent', 'Möchten Sie diesen Termin wirklich löschen?'), danger: true })) {
                try {
                  setOperationInProgress(true);
                  setOperationMessage(t('calendar.deletingEvent', 'Termin wird gelöscht...'));
                  await apiService.deleteCalendarEvent(ev.id);
                  await loadCalendarDataSilently();
                } catch (err) {
                  console.error('Error deleting event:', err);
                  pushToast({ type: 'error', message: t('calendar.deleteError', 'Fehler beim Löschen des Termins.') });
                } finally {
                  setOperationInProgress(false);
                  setOperationMessage('');
                }
              }
            }}
          >
            🗑️ {t('calendar.deleteEvent', 'Eintrag löschen')}
          </div>
        </div>
      )}

      {showEventConvertModal && selectedEventForConvert && (
        <EventToTicketModal
          visible={showEventConvertModal}
          onClose={() => { setShowEventConvertModal(false); setSelectedEventForConvert(null); }}
          event={selectedEventForConvert}
          currentUser={currentUser}
          onSuccess={async () => { try { await loadCalendarDataSilently(); } catch (_) {} }}
        />
      )}
      
      {/* OPERATION IN PROGRESS OVERLAY */}
      {operationInProgress && (
        <div 
          className="operation-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'var(--color-backdrop-10)',
            zIndex: 5000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none'
          }}
        >
          <div 
            style={{
              background: 'var(--color-cal-drag-hinweis)',
              color: 'white',
              padding: '16px 24px',
              borderRadius: '8px',
              boxShadow: '0 4px 20px var(--color-shadow-30)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            <RefreshCw className="animate-spin" size={20} />
            {operationMessage}
          </div>
        </div>
      )}

      {/* DROP CHOICE MODAL */}
      {dropChoiceModal.visible && dropChoiceModal.context && (
        <Modal open onClose={() => setDropChoiceModal({ visible: false, context: null })} title={t('calendar.dropChoice.title', 'Termin verschieben oder neuen erstellen?')} maxWidth="560px">
              <p style={{ marginBottom: 12 }}>
                {t('calendar.dropChoice.text', 'Sie haben einen bestehenden Termin bewegt. Wie möchten Sie fortfahren?')}
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={async () => {
                    const ctx = dropChoiceModal.context;
                    if (!ctx) { setDropChoiceModal({ visible: false, context: null }); return; }
                    setDropChoiceModal({ visible: false, context: null });
                    // Verschieben: wie bisher
                    try {
                      setOperationInProgress(true);
                      setOperationMessage(t('calendar.updating', 'Wird aktualisiert...'));
                      const ticket = ctx.ticket;
                      const originalStart = new Date(ticket.plannedStartDateTime);
                      const originalEnd = new Date(ticket.plannedEndDateTime);
                      const duration = originalEnd - originalStart;
                      const newStartDateTime = new Date(ctx.newStartDateTime);
                      const newEndDateTime = new Date(newStartDateTime.getTime() + duration);
                      const planningUpdateData = {
                        startDateTime: newStartDateTime.toISOString(),
                        endDateTime: newEndDateTime.toISOString(),
                        isPlanned: true
                      };
                      if (currentUser) {
                        planningUpdateData.assignedTo = currentUser.mail || currentUser.userPrincipalName;
                      }
                      await apiService.updateTicketPlanning(ticket.id, planningUpdateData);
                      await loadCalendarDataSilently();
                      // Sidebar sofort aktualisieren, falls Ursprung Sidebar (überfällig)
                      try {
                        if (ctx && ctx.origin === 'sidebar' && removeTicketFromSidebar.current?.removeTicket) {
                          removeTicketFromSidebar.current.removeTicket(ticket.id);
                          console.log('📅 [CALENDAR] Removed moved planned ticket from sidebar via modal (origin sidebar)');
                        }
                      } catch (_) {}
                    } catch (err) {
                      console.error('DropChoice move error:', err);
                      setError(t('calendar.updateError', 'Fehler beim Aktualisieren.'));
                    } finally {
                      setOperationInProgress(false);
                      setOperationMessage('');
                    }
                  }}
                >
                  {t('calendar.dropChoice.move', 'Verschieben')}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    const ctx = dropChoiceModal.context;
                    if (!ctx) { setDropChoiceModal({ visible: false, context: null }); return; }
                    setDropChoiceModal({ visible: false, context: null });
                    // Weiteren erstellen: Projekt ohne Untervorgänge, Block1 = Original, Block2 = neuer an Ziel
                    try {
                      setOperationInProgress(true);
                      setOperationMessage(t('calendar.creating', 'Erstelle...'));
                      const srcTicket = ctx.ticket;
                      // 0) Prüfen: gehört Ticket bereits zu Projekt/Subprojekt?
                      let projectId = null;
                      if ((srcTicket.projectId && Number(srcTicket.projectId) > 0) || (srcTicket.subprojectId && Number(srcTicket.subprojectId) > 0)) {
                        projectId = Number(srcTicket.projectId) || null;
                      } else {
                        // 1) Projekt anlegen (ohne Untervorgänge)
                        const projectName = srcTicket.title || 'Projekt';
                        // #158: Kunde des Quelltickets auf den Auto-Vorgang übernehmen,
                        // sonst hat die #103-Vererbung für Folgetickets nichts zu erben.
                        const projectRes = await apiService.createProject({ name: projectName, description: '', responsibleEmail: null, status: 'wartend', turnusCount: null, turnusEvery: null, turnusUnit: null, subprojects: [], isAutoCreated: true, customerId: srcTicket.customerId || null });
                        projectId = projectRes?.project?.id;
                        if (!projectId) throw new Error('Projekt konnte nicht erstellt werden');
                      }

                      // 2) Originaltitel und neuer Blocktitel ermitteln
                      const { baseTitle, existingBlock } = parseBlockSuffix(srcTicket.title);
                      // Normalisiere auf "Base - (n)"
                      const normalizedBase = baseTitle.replace(/\s*-\s*$/, '');
                      const shouldRenameOriginal = (existingBlock == null);
                      const newOriginalTitle = shouldRenameOriginal ? `${normalizedBase} - (1)` : srcTicket.title;
                      await apiService.updateTicket(srcTicket.id, { title: newOriginalTitle, projectId, subprojectId: null });

                      // 3) Neues Ticket (Block 2) mit gleichem Titel, gleicher Zuordnung/Marker anlegen
                      const nextBlockNum = (existingBlock ? existingBlock + 1 : 2);
                      const block2Title = `${normalizedBase} - (${nextBlockNum})`;
                      const assignedTo = srcTicket.assignedTo || (currentUser?.mail || currentUser?.userPrincipalName || '');
                      const created2 = await apiService.createTicket({
                        title: block2Title,
                        description: srcTicket.description || '',
                        priority: srcTicket.priority || 'normal',
                        assignedTo: assignedTo || '',
                        category: srcTicket.category || 'Projekt',
                        tags: srcTicket.tags || [],
                        calenderMarker: srcTicket.calenderMarker || null,
                        projectId: projectId,
                        subprojectId: null,
                        // #158: Kunde explizit mitgeben — deckt auch den Zweig ab, in dem
                        // das Quellticket schon in einem Vorgang ohne Kunden hängt.
                        customerId: srcTicket.customerId || null,
                        // #105: Ansprechpartner mit vererben (Server validiert gegen den Kunden)
                        contactId: srcTicket.contactId || null
                      });
                      const block2Ticket = created2.ticket || created2;

                      // 4) Block 2 am Drop-Ziel terminieren
                      const originalStart = new Date(srcTicket.plannedStartDateTime);
                      const originalEnd = new Date(srcTicket.plannedEndDateTime);
                      const duration = originalEnd - originalStart;
                      const newStartDateTime = new Date(ctx.newStartDateTime);
                      const newEndDateTime = new Date(newStartDateTime.getTime() + duration);
                      const planningData = {
                        startDateTime: newStartDateTime.toISOString(),
                        endDateTime: newEndDateTime.toISOString(),
                        assignedTo: assignedTo || undefined
                      };
                      await apiService.planTicket(block2Ticket.id, planningData);

                      // 5) Refresh
                      await loadCalendarDataSilently();
                      try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'drop_choice_duplicate' } })); } catch (_) {}
                    } catch (err) {
                      console.error('DropChoice duplicate error:', err);
                      setError(t('calendar.createError', 'Fehler beim Erstellen.'));
                    } finally {
                      setOperationInProgress(false);
                      setOperationMessage('');
                    }
                  }}
                >
                  {t('calendar.dropChoice.createAnother', 'Weiteren erstellen')}
                </button>
              </div>
        </Modal>
      )}

      {/* CREATE PROJECT WIZARD entfernt */}

    </div>
  );
};

export default CalendarView; 